import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Checkbox,
  Link,
  IconButton,
  MenuItem,
  Paper,
  Popover,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  InfoOutlined as InfoOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  LayersOutlined as LayersOutlinedIcon,
} from '@mui/icons-material';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import {
  approvePublishedBatch,
  createPublishedScheduleBatch,
  fetchPublishedSchedules,
  loadPublishEmailPref,
  publishPublishedBatch,
  rejectPublishedBatch,
  savePublishEmailPref,
  submitPublishedBatchForApproval,
} from './publishedScheduleApi';
import EmployeeCoverageDialog from './EmployeeCoverageDialog';
import WorkforceWeekSummaryPanels from './WorkforceWeekSummaryPanels';
import { resolveWorkforceScope } from './workforceBusinessUnitUtils';
import { fetchWorkAreaLabelMap, formatPositionLabel } from './workforcePositionUtils';
import {
  DEFAULT_WEEK_END_DAY,
  DEFAULT_WEEK_START_DAY,
  buildWorkWeekDays,
  dateForCoverageDayInWeek,
  fetchTenantWorkWeek,
  getMonthGridStartOffset,
  getWeekStartDate,
  rotateWeekDays,
} from './workforceWeekUtils';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateCardSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSwitchSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const brandGreen = ht.green;
const brandGreenDark = ht.green;
const brandGreenSoft = 'rgba(2, 107, 29, 0.06)';
const brandGreenBorder = 'rgba(2, 107, 29, 0.22)';
/** Day role-row aggregate pills: distinct from employee shift fills; coverage stays on left stripe. */
const ROLE_SUMMARY_SHIFT_FILL = '#E2EAF4';
/** Day view: full role position header row (label + timeline). */
const TIMELINE_DAY_ROLE_POSITION_ROW_BG = '#E4EBF5';
/** Day view: employee + coverage-needed rows under a role. */
const TIMELINE_DAY_RESOURCE_ROW_BG = '#FAFAF8';
/** Filled green toolbar actions (weekly schedule bar) */
const scheduleToolbarContainedGreenSx = harmonateProceedButtonSx;
const scheduleToolbarOutlinedGreenSx = {
  color: ht.green,
  borderColor: brandGreenBorder,
  '&:hover': { borderColor: ht.green, bgcolor: brandGreenSoft },
};
const scheduleViewModeContainedSx = {
  ...harmonateProceedButtonSx,
  borderColor: ht.green,
  boxShadow: 'none',
  '&:hover': { ...harmonateProceedButtonSx['&:hover'], borderColor: ht.green },
};

const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toIsoMonth = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  return `${year}-${month}`;
};

const getIsoWeekString = (baseDate) => {
  const d = new Date(baseDate);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const isoYear = d.getFullYear();
  const week1 = new Date(isoYear, 0, 4);
  const weekNo = 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
  return `${isoYear}-W${String(weekNo).padStart(2, '0')}`;
};

const getMondayFromIsoWeek = (isoWeek) => {
  const match = /^(\d{4})-W(\d{2})$/.exec(isoWeek || '');
  if (!match) {
    return getWeekStartMonday(new Date());
  }
  const year = Number(match[1]);
  const week = Number(match[2]);
  const jan4 = new Date(year, 0, 4);
  const jan4Weekday = (jan4.getDay() + 6) % 7;
  const mondayWeek1 = new Date(year, 0, 4 - jan4Weekday);
  const monday = new Date(mondayWeek1);
  monday.setDate(mondayWeek1.getDate() + (week - 1) * 7);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const getWeekStartMonday = (baseDate) => {
  const d = new Date(baseDate);
  const jsDay = d.getDay();
  const distance = jsDay === 0 ? -6 : 1 - jsDay;
  d.setDate(d.getDate() + distance);
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatDisplayDate = (date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const formatDisplayTime = (value) => {
  if (!value || typeof value !== 'string') return '';
  const [h, m] = value.split(':');
  const hour = Number(h);
  if (Number.isNaN(hour) || m == null) return value;
  const normalized = hour % 12 || 12;
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${normalized}:${m} ${suffix}`;
};

const buildShiftText = (item, dayPrefix) => {
  const start = item[`${dayPrefix}StartTime`];
  const end = item[`${dayPrefix}EndTime`];
  if (!start || !end) return null;
  const code = item.shiftCode || item.shiftName || 'Shift';
  return `${code} ${formatDisplayTime(start)}-${formatDisplayTime(end)}`;
};

const getDayPayload = (item, dayPrefix) => ({
  startTime: item[`${dayPrefix}StartTime`],
  endTime: item[`${dayPrefix}EndTime`],
  requiredCount: item[`${dayPrefix}Required`] ?? 0,
});

const toTimeKey = (value) => (value && value.length === 5 ? `${value}:00` : value);
const parseIsoDate = (value) => new Date(`${value}T00:00:00`);
const normalizeDay = (value) => (value || '').toString().trim().toUpperCase();

const weekdayKeyLowerFromIso = (isoDate) =>
  parseIsoDate(isoDate).toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();

const flexibleTimeToMinutes = (value) => {
  if (value == null || value === '') return null;
  const s = String(value).trim().slice(0, 8);
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(min)) return null;
  return h * 60 + min;
};

const normalizeApiDateToIso = (d) => {
  if (d == null || d === '') return '';
  if (typeof d === 'string') return d.length >= 10 ? d.slice(0, 10) : d;
  if (Array.isArray(d) && d.length >= 3) {
    return `${d[0]}-${String(d[1]).padStart(2, '0')}-${String(d[2]).padStart(2, '0')}`;
  }
  return '';
};

/** Shift must fall inside preferred window; duration must not exceed total_hours when positive. */
const isEmployeeAvailableForDate = (availability, isoDate, shiftStart, shiftEnd) => {
  if (!availability || typeof availability !== 'object') return true;
  const dayName = normalizeDay(parseIsoDate(isoDate).toLocaleDateString('en-US', { weekday: 'long' }));
  const dayLower = weekdayKeyLowerFromIso(isoDate);

  const preferredObj = availability.preferred_day;
  if (preferredObj && typeof preferredObj === 'object' && !Array.isArray(preferredObj) && Object.keys(preferredObj).length) {
    const slot = preferredObj[dayLower];
    if (!slot || typeof slot !== 'object') return false;
    const ps = flexibleTimeToMinutes(slot.start_time);
    const pe = flexibleTimeToMinutes(slot.end_time);
    if (ps == null || pe == null || pe <= ps) return false;
    const ss = flexibleTimeToMinutes(toTimeKey(shiftStart));
    const se = flexibleTimeToMinutes(toTimeKey(shiftEnd));
    if (ss == null || se == null || ss === se) return false;
    const overnight = se < ss;
    if (!overnight && (ss < ps || se > pe)) return false;
    const cap = Number(slot.total_hours);
    if (Number.isFinite(cap) && cap > 0) {
      let durH = (se - ss) / 60;
      if (durH <= 0) durH += 24;
      if (durH > cap + 1e-6) return false;
    }
    return true;
  }

  const availableLower = Array.isArray(availability.available) ? availability.available.map(normalizeDay) : null;
  if (availableLower) {
    if (availableLower.length === 0) return true;
    return availableLower.includes(dayName);
  }

  const available = Array.isArray(availability.AVAILABLE) ? availability.AVAILABLE.map(normalizeDay) : null;
  const preferred = Array.isArray(availability.PREFERRED_DAY) ? availability.PREFERRED_DAY.map(normalizeDay) : null;
  const offDay = Array.isArray(availability.OFF_DAY) ? availability.OFF_DAY.map(normalizeDay) : null;

  if (available) {
    if (available.length === 0) return true;
    return available.includes(dayName);
  }
  if (preferred) return preferred.includes(dayName);
  if (offDay) return !offDay.includes(dayName);
  return true;
};

const enumerateDatesInclusive = (startYmd, endYmd) => {
  if (!startYmd) return [];
  const end = endYmd || startYmd;
  const dates = [];
  const cur = parseIsoDate(startYmd);
  const endDate = parseIsoDate(end);
  while (cur <= endDate) {
    dates.push(toIsoDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};

const buildEmployeeLeaveKeys = (leaveList, rangeStart, rangeEnd) => {
  const keys = new Set();
  (Array.isArray(leaveList) ? leaveList : []).forEach((lv) => {
    const eid = lv.employeeId;
    if (!eid) return;
    const from = normalizeApiDateToIso(lv.leaveFromDate ?? lv.leaveDate);
    const to = normalizeApiDateToIso(lv.leaveToDate ?? lv.leaveFromDate ?? lv.leaveDate);
    if (!from) return;
    enumerateDatesInclusive(from, to || from).forEach((iso) => {
      if (!rangeStart || !rangeEnd || (iso >= rangeStart && iso <= rangeEnd)) {
        keys.add(`${eid}|${iso}`);
      }
    });
  });
  return keys;
};

const LEAVE_CONFLICT_VISUAL = { label: 'On leave', bg: '#FFEBEE', color: '#C62828' };

const isEmployeeOnLeaveForDate = (employeeId, dateIso, leaveKeys) => {
  if (!employeeId || !dateIso || !leaveKeys) return false;
  return leaveKeys.has(`${employeeId}|${dateIso}`);
};

const withLeaveConflictVisual = (cv, leaveConflict) =>
  leaveConflict ? { ...cv, ...LEAVE_CONFLICT_VISUAL } : cv;

const leaveConflictPaperSx = (baseSx, leaveConflict) =>
  leaveConflict
    ? {
        ...baseSx,
        bgcolor: LEAVE_CONFLICT_VISUAL.bg,
        borderLeft: `3px solid ${LEAVE_CONFLICT_VISUAL.color}`,
        borderColor: '#EF9A9A',
        boxShadow: 'inset 0 0 0 1px rgba(198, 40, 40, 0.35)',
      }
    : baseSx;

const getLeaveRangeForView = (viewMode, weekStartDate, weekEndDate, selectedDayDate, monthAnchorDate) => {
  if (viewMode === 'day') {
    return { start: selectedDayDate, end: selectedDayDate };
  }
  if (viewMode === 'monthly' && monthAnchorDate) {
    const first = toIsoDate(new Date(monthAnchorDate.getFullYear(), monthAnchorDate.getMonth(), 1));
    const last = toIsoDate(new Date(monthAnchorDate.getFullYear(), monthAnchorDate.getMonth() + 1, 0));
    return { start: first, end: last };
  }
  return { start: weekStartDate, end: weekEndDate };
};

const HOURS_EPSILON = 0.01;

const resolveRowAssignedHours = (row) => {
  if (!row?.startTime || !row?.endTime) return 0;
  const sm = flexibleTimeToMinutes(toTimeKey(row.startTime));
  const em = flexibleTimeToMinutes(toTimeKey(row.endTime));
  if (sm == null || em == null) return 0;
  let minutes = em - sm;
  if (minutes <= 0) minutes += 24 * 60;
  let hours = minutes / 60;
  if (row.allocation === 'PARTIAL') hours /= 2;
  return Math.round(hours * 100) / 100;
};

const hoursFromAssignmentRecord = (assignment) => {
  if (!assignment) return 0;
  if (assignment.assignedHours != null && assignment.assignedHours !== '') {
    return Math.round(Number(assignment.assignedHours) * 100) / 100;
  }
  return resolveRowAssignedHours({
    startTime: assignment.startTime,
    endTime: assignment.endTime,
    allocation: assignment.assignmentType === 'PARTIAL' ? 'PARTIAL' : 'FULL',
  });
};

const getWeekBoundsForDate = (isoDate, startDay = DEFAULT_WEEK_START_DAY) => {
  const start = getWeekStartDate(parseIsoDate(isoDate), startDay);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return { weekStart: toIsoDate(start), weekEnd: toIsoDate(end) };
};

const buildSlotKey = (item) =>
  [
    item.businessDate || '',
    item.rolePositionId || '',
    toTimeKey(item.startTime) || '',
    toTimeKey(item.endTime) || '',
    item.scheduleId || '',
    item.workforceScheduleId || item.coverageId || '',
  ].join('|');

const getLeaveConflictEmployeeIdsForEntry = (entry, assignmentEmployeeIdsBySlotKey, leaveKeys) => {
  if (!entry || !leaveKeys?.size) return [];
  const dateIso = normalizeApiDateToIso(entry.businessDate);
  if (!dateIso) return [];
  const key = buildSlotKey(entry);
  const ids = assignmentEmployeeIdsBySlotKey[key] || [];
  return ids.filter((id) => isEmployeeOnLeaveForDate(id, dateIso, leaveKeys));
};

const computeEmployeeWeeklyHoursForProposal = (
  employeeId,
  row,
  workingRows,
  weekAssignments,
  startDay = DEFAULT_WEEK_START_DAY
) => {
  const { weekStart, weekEnd } = getWeekBoundsForDate(row.businessDate, startDay);
  const dialogSlotKeys = new Set(workingRows.map((r) => r.slotKey));
  let total = 0;

  workingRows.forEach((other) => {
    const assignedId = other.rowKey === row.rowKey ? employeeId : other.employeeId;
    if (assignedId !== employeeId) return;
    if (!other.businessDate || other.businessDate < weekStart || other.businessDate > weekEnd) return;
    total += resolveRowAssignedHours(other);
  });

  (weekAssignments || []).forEach((assignment) => {
    if (dialogSlotKeys.has(buildSlotKey(assignment))) return;
    if (assignment.employeeId !== employeeId) return;
    if (!assignment.businessDate || assignment.businessDate < weekStart || assignment.businessDate > weekEnd) {
      return;
    }
    const status = normalizeDay(assignment.assignmentStatus);
    if (status === 'CANCELLED' || status === 'VOID') return;
    total += resolveRowAssignedHours(assignment);
  });

  return Math.round(total * 100) / 100;
};

const isEmployeeOvertimeAllowed = (employee) => {
  if (!employee) return false;
  const isSalaried = employee.isSalaried === true || employee.is_salaried === 'YES' || employee.is_salaried === true;
  if (isSalaried) return false;
  const ot = employee.isOvertimeAllowed ?? employee.is_overtime_allowed;
  return ot === true || ot === 'YES';
};

const getMaxHoursStatus = (
  employeeId,
  row,
  workingRows,
  weekAssignments,
  employeesById,
  startDay = DEFAULT_WEEK_START_DAY
) => {
  if (!employeeId) return { status: 'ok' };
  const employee = employeesById[employeeId];
  // OT-eligible employees are not capped by Max Hours / Week in Assign.
  if (isEmployeeOvertimeAllowed(employee)) return { status: 'ok' };
  const maxHours = employee?.maxHoursPerWeek ?? employee?.max_hours_per_week;
  if (maxHours == null || Number(maxHours) <= 0) return { status: 'ok' };

  const projected = computeEmployeeWeeklyHoursForProposal(
    employeeId,
    row,
    workingRows,
    weekAssignments,
    startDay
  );
  const max = Number(maxHours);
  if (projected > max + HOURS_EPSILON) {
    return { status: 'over', projectedHours: projected, maxHours: max };
  }
  if (projected >= max - HOURS_EPSILON) {
    return { status: 'warning', projectedHours: projected, maxHours: max };
  }
  return { status: 'ok', projectedHours: projected, maxHours: max };
};

const computeEmployeeDailyHoursForProposal = (employeeId, row, workingRows, weekAssignments) => {
  const date = row.businessDate;
  const dialogSlotKeys = new Set(workingRows.map((r) => r.slotKey));
  let total = 0;

  workingRows.forEach((other) => {
    const assignedId = other.rowKey === row.rowKey ? employeeId : other.employeeId;
    if (assignedId !== employeeId || other.businessDate !== date) return;
    total += resolveRowAssignedHours(other);
  });

  (weekAssignments || []).forEach((assignment) => {
    if (dialogSlotKeys.has(buildSlotKey(assignment))) return;
    if (assignment.employeeId !== employeeId || assignment.businessDate !== date) return;
    const status = normalizeDay(assignment.assignmentStatus);
    if (status === 'CANCELLED' || status === 'VOID') return;
    total += resolveRowAssignedHours(assignment);
  });

  return Math.round(total * 100) / 100;
};

const buildAssignmentRowsFromSchedule = (scheduleRows, assignmentList) => {
  const existingBySlot = {};
  (Array.isArray(assignmentList) ? assignmentList : []).forEach((assignment) => {
    const key = buildSlotKey(assignment);
    const list = existingBySlot[key] || [];
    list.push(assignment);
    existingBySlot[key] = list;
  });
  const rows = [];
  const sorted = [...scheduleRows].sort((a, b) =>
    `${a.businessDate}|${toTimeKey(a.startTime)}`.localeCompare(`${b.businessDate}|${toTimeKey(b.startTime)}`)
  );
  sorted.forEach((slot) => {
    const required = Math.max(Number(slot.requiredCount) || 0, 1);
    const slotKey = buildSlotKey(slot);
    const existingForSlot = existingBySlot[slotKey] || [];
    for (let i = 0; i < required; i += 1) {
      const existingAssignment = existingForSlot[i];
      rows.push({
        rowKey: `${slotKey}#${i + 1}`,
        slotKey,
        workforceScheduleId: slot.coverageId,
        scheduleId: slot.scheduleId || null,
        businessDate: slot.businessDate,
        dayName: slot.dayName,
        rolePositionId: slot.rolePositionId,
        roleLabel: slot.roleLabel,
        startTime: toTimeKey(slot.startTime),
        endTime: toTimeKey(slot.endTime),
        shiftLabel: `${slot.shiftCode || slot.shiftName || 'Shift'} ${formatDisplayTime(toTimeKey(slot.startTime))}-${formatDisplayTime(
          toTimeKey(slot.endTime)
        )}`,
        requiredCount: Number(slot.requiredCount) || 0,
        employeeAssignmentId: existingAssignment?.employeeAssignmentId || null,
        employeeId: existingAssignment?.employeeId || '',
        allocation: existingAssignment?.assignmentType === 'PARTIAL' ? 'PARTIAL' : 'FULL',
        assignmentStatus: existingAssignment?.assignmentStatus || 'ASSIGNED',
        assignmentType: existingAssignment?.assignmentType || 'MANUAL',
        gmOverrideApproved: existingAssignment?.isApproved === true,
        isApproved: existingAssignment?.isApproved === true,
      });
    }
  });
  return rows;
};

const shiftIntervalEndMs = (businessDate, startTime, endTime) => {
  const s = new Date(`${businessDate}T${toTimeKey(startTime)}`).getTime();
  let e = new Date(`${businessDate}T${toTimeKey(endTime)}`).getTime();
  if (e <= s) e += 86400000;
  return e;
};

const hhmmFromDate = (d) =>
  formatDisplayTime(
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`
  );

const shortDateLabel = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

/** One shift row: name + time window (Outlook-style line). */
const formatRoleShiftLine = (entry) => {
  const sd = entry.businessDate;
  if (!sd) return '';
  const sk = toTimeKey(entry.startTime);
  if (!sk) return '';
  const name = String(entry.shiftCode || entry.shiftName || 'Shift').trim() || 'Shift';
  const startMs = new Date(`${sd}T${sk}`).getTime();
  const endMs = shiftIntervalEndMs(sd, entry.startTime, entry.endTime);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return '';
  const startD = new Date(startMs);
  const endD = new Date(endMs);
  const t1 = hhmmFromDate(startD);
  const t2 = hhmmFromDate(endD);
  const sameDay =
    startD.getFullYear() === endD.getFullYear() &&
    startD.getMonth() === endD.getMonth() &&
    startD.getDate() === endD.getDate();
  if (sameDay) {
    return `${name} ${t1} – ${t2}`;
  }
  return `${name} ${shortDateLabel(startD)} ${t1} – ${shortDateLabel(endD)} ${t2}`;
};

const roleShiftDedupKey = (entry) =>
  [
    entry.businessDate || '',
    toTimeKey(entry.startTime) || '',
    toTimeKey(entry.endTime) || '',
    String(entry.shiftCode || entry.shiftName || 'Shift').trim(),
  ].join('|');

/**
 * Compact one-line summary + full multi-line text for tooltip (deduped, Outlook-like).
 */
const buildRoleScheduleSummary = (entries) => {
  if (!entries?.length) return { compact: '', full: '', shiftCount: 0 };
  const sorted = [...entries].sort((a, b) =>
    `${a.businessDate}|${toTimeKey(a.startTime)}`.localeCompare(`${b.businessDate}|${toTimeKey(b.startTime)}`)
  );

  const counts = new Map();
  const keyOrder = [];
  const keyToEntry = new Map();
  sorted.forEach((e) => {
    const line = formatRoleShiftLine(e);
    if (!line) return;
    const k = roleShiftDedupKey(e);
    counts.set(k, (counts.get(k) || 0) + 1);
    if (!keyToEntry.has(k)) {
      keyToEntry.set(k, e);
      keyOrder.push(k);
    }
  });

  const lines = keyOrder.map((k) => {
    const entry = keyToEntry.get(k);
    const line = formatRoleShiftLine(entry);
    const c = counts.get(k) || 1;
    return c > 1 ? `${c}× ${line}` : line;
  });

  if (!lines.length) return { compact: '', full: '', shiftCount: sorted.length };

  const shiftCount = sorted.length;
  const shiftWord = shiftCount === 1 ? 'shift' : 'shifts';
  const full = [`${shiftCount} ${shiftWord} scheduled`, ...lines].join('\n');

  const MAX_VISIBLE = 2;
  let compactMiddle;
  if (lines.length <= MAX_VISIBLE) {
    compactMiddle = lines.join(' · ');
  } else {
    compactMiddle = `${lines.slice(0, MAX_VISIBLE).join(' · ')} · +${lines.length - MAX_VISIBLE} more`;
  }
  const compact = `${shiftCount} ${shiftWord} · ${compactMiddle}`;

  return { compact, full, shiftCount };
};

/** Same business day anchor; end may be the next calendar day when clock end is before start. */
const isTimeOverlap = (businessDate, startA, endA, startB, endB) => {
  const a0 = new Date(`${businessDate}T${toTimeKey(startA)}`).getTime();
  const a1 = shiftIntervalEndMs(businessDate, startA, endA);
  const b0 = new Date(`${businessDate}T${toTimeKey(startB)}`).getTime();
  const b1 = shiftIntervalEndMs(businessDate, startB, endB);
  return a0 < b1 && b0 < a1;
};

const LEGACY_AUTO_ASSIGN_POLICY = {
  enabled: false,
  selectionMode: 'EMPLOYEE_ID_ASC',
  maxShiftsPerDay: null,
  minRestHoursBetweenShifts: null,
  maxConsecutiveWorkDays: null,
  preferPreferredAvailability: true,
};

const mapAutoAssignPolicyFromApi = (item) => {
  if (!item || typeof item !== 'object') return { ...LEGACY_AUTO_ASSIGN_POLICY };
  const toNullableNumber = (value) => {
    if (value === '' || value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  return {
    enabled: item.enabled === true,
    selectionMode: String(item.selection_mode || item.selectionMode || 'EMPLOYEE_ID_ASC').toUpperCase(),
    maxShiftsPerDay: toNullableNumber(item.max_shifts_per_day ?? item.maxShiftsPerDay),
    minRestHoursBetweenShifts: toNullableNumber(
      item.min_rest_hours_between_shifts ?? item.minRestHoursBetweenShifts
    ),
    maxConsecutiveWorkDays: toNullableNumber(
      item.max_consecutive_work_days ?? item.maxConsecutiveWorkDays
    ),
    preferPreferredAvailability:
      item.prefer_preferred_availability ?? item.preferPreferredAvailability ?? true,
  };
};

const collectEmployeeAssignmentSlots = (employeeId, row, workingRows, weekAssignments) => {
  const dialogSlotKeys = new Set(workingRows.map((r) => r.slotKey));
  const slots = [];

  workingRows.forEach((other) => {
    const assignedId = other.rowKey === row.rowKey ? employeeId : other.employeeId;
    if (assignedId !== employeeId) return;
    if (!other.businessDate || !other.startTime || !other.endTime) return;
    slots.push({
      businessDate: other.businessDate,
      startTime: other.startTime,
      endTime: other.endTime,
      rowKey: other.rowKey,
    });
  });

  (weekAssignments || []).forEach((assignment) => {
    if (dialogSlotKeys.has(buildSlotKey(assignment))) return;
    if (assignment.employeeId !== employeeId) return;
    const status = normalizeDay(assignment.assignmentStatus);
    if (status === 'CANCELLED' || status === 'VOID') return;
    if (!assignment.businessDate || !assignment.startTime || !assignment.endTime) return;
    slots.push({
      businessDate: assignment.businessDate,
      startTime: assignment.startTime,
      endTime: assignment.endTime,
      rowKey: assignment.assignmentId || buildSlotKey(assignment),
    });
  });

  return slots;
};

const countEmployeeShiftsOnDate = (employeeId, date, row, workingRows, weekAssignments) =>
  collectEmployeeAssignmentSlots(employeeId, row, workingRows, weekAssignments).filter(
    (slot) => slot.businessDate === date
  ).length;

const hoursBetweenShiftIntervals = (dateA, startA, endA, dateB, startB, endB) => {
  const a0 = new Date(`${dateA}T${toTimeKey(startA)}`).getTime();
  const a1 = shiftIntervalEndMs(dateA, startA, endA);
  const b0 = new Date(`${dateB}T${toTimeKey(startB)}`).getTime();
  const b1 = shiftIntervalEndMs(dateB, startB, endB);
  if (!Number.isFinite(a0) || !Number.isFinite(a1) || !Number.isFinite(b0) || !Number.isFinite(b1)) {
    return Number.POSITIVE_INFINITY;
  }
  if (a0 < b1 && b0 < a1) return 0;
  if (a1 <= b0) return (b0 - a1) / 3600000;
  return (a0 - b1) / 3600000;
};

const employeeViolatesMinRest = (employeeId, row, workingRows, weekAssignments, minRestHours) => {
  if (minRestHours == null || Number(minRestHours) < 0) return false;
  const minRest = Number(minRestHours);
  const slots = collectEmployeeAssignmentSlots(employeeId, row, workingRows, weekAssignments);
  return slots.some((slot) => {
    if (slot.rowKey === row.rowKey) return false;
    const gap = hoursBetweenShiftIntervals(
      row.businessDate,
      row.startTime,
      row.endTime,
      slot.businessDate,
      slot.startTime,
      slot.endTime
    );
    return gap + 1e-6 < minRest;
  });
};

const employeeViolatesMaxConsecutiveDays = (
  employeeId,
  row,
  workingRows,
  weekAssignments,
  maxConsecutiveWorkDays
) => {
  if (maxConsecutiveWorkDays == null || Number(maxConsecutiveWorkDays) < 1) return false;
  const maxDays = Number(maxConsecutiveWorkDays);
  const workDates = new Set(
    collectEmployeeAssignmentSlots(employeeId, row, workingRows, weekAssignments).map(
      (slot) => slot.businessDate
    )
  );
  workDates.add(row.businessDate);

  const sorted = [...workDates].sort();
  let streak = 1;
  let maxStreak = 1;
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = parseIsoDate(sorted[i - 1]);
    const curr = parseIsoDate(sorted[i]);
    const diffDays = Math.round((curr.getTime() - prev.getTime()) / 86400000);
    if (diffDays === 1) {
      streak += 1;
      maxStreak = Math.max(maxStreak, streak);
    } else {
      streak = 1;
    }
  }
  return maxStreak > maxDays;
};

const employeeHasPreferredDayMatch = (employee, businessDate) => {
  const availability = employee?.availability;
  if (!availability || typeof availability !== 'object') return false;
  const dayName = normalizeDay(parseIsoDate(businessDate).toLocaleDateString('en-US', { weekday: 'long' }));
  const dayLower = weekdayKeyLowerFromIso(businessDate);
  const preferredObj = availability.preferred_day;
  if (preferredObj && typeof preferredObj === 'object' && !Array.isArray(preferredObj)) {
    return Boolean(preferredObj[dayLower]);
  }
  const preferred = Array.isArray(availability.PREFERRED_DAY)
    ? availability.PREFERRED_DAY.map(normalizeDay)
    : null;
  return preferred ? preferred.includes(dayName) : false;
};

const countEmployeeWeeklyShiftsForProposal = (
  employeeId,
  row,
  workingRows,
  weekAssignments,
  startDay = DEFAULT_WEEK_START_DAY
) => {
  const { weekStart, weekEnd } = getWeekBoundsForDate(row.businessDate, startDay);
  return collectEmployeeAssignmentSlots(employeeId, row, workingRows, weekAssignments).filter(
    (slot) => slot.businessDate >= weekStart && slot.businessDate <= weekEnd
  ).length;
};

const passesAutoAssignPolicyConstraints = (
  employeeId,
  row,
  workingRows,
  weekAssignments,
  policy
) => {
  if (!policy?.enabled) return true;
  if (policy.maxShiftsPerDay != null && policy.maxShiftsPerDay >= 1) {
    if (
      countEmployeeShiftsOnDate(employeeId, row.businessDate, row, workingRows, weekAssignments) >
      policy.maxShiftsPerDay
    ) {
      return false;
    }
  }
  if (
    employeeViolatesMinRest(
      employeeId,
      row,
      workingRows,
      weekAssignments,
      policy.minRestHoursBetweenShifts
    )
  ) {
    return false;
  }
  if (
    employeeViolatesMaxConsecutiveDays(
      employeeId,
      row,
      workingRows,
      weekAssignments,
      policy.maxConsecutiveWorkDays
    )
  ) {
    return false;
  }
  return true;
};

const pickAutoAssignEmployee = (
  candidates,
  row,
  workingRows,
  weekAssignments,
  employeesById,
  policy,
  passAssignCounts,
  startDay = DEFAULT_WEEK_START_DAY
) => {
  if (!candidates.length) return null;
  if (!policy?.enabled) return candidates[0];

  const mode = policy.selectionMode || 'EMPLOYEE_ID_ASC';
  const scored = candidates.map((employeeId) => {
    const weeklyHours = computeEmployeeWeeklyHoursForProposal(
      employeeId,
      row,
      workingRows,
      weekAssignments,
      startDay
    );
    const weeklyShifts = countEmployeeWeeklyShiftsForProposal(
      employeeId,
      row,
      workingRows,
      weekAssignments,
      startDay
    );
    const preferredBoost =
      policy.preferPreferredAvailability &&
      employeeHasPreferredDayMatch(employeesById[employeeId], row.businessDate)
        ? 0
        : 1;
    const roundRobinCount = passAssignCounts[employeeId] || 0;
    return { employeeId, weeklyHours, weeklyShifts, preferredBoost, roundRobinCount };
  });

  scored.sort((a, b) => {
    if (a.preferredBoost !== b.preferredBoost) return a.preferredBoost - b.preferredBoost;
    if (mode === 'LEAST_WEEKLY_HOURS') {
      if (a.weeklyHours !== b.weeklyHours) return a.weeklyHours - b.weeklyHours;
      if (a.weeklyShifts !== b.weeklyShifts) return a.weeklyShifts - b.weeklyShifts;
    } else if (mode === 'LEAST_WEEKLY_SHIFTS') {
      if (a.weeklyShifts !== b.weeklyShifts) return a.weeklyShifts - b.weeklyShifts;
      if (a.weeklyHours !== b.weeklyHours) return a.weeklyHours - b.weeklyHours;
    } else if (mode === 'ROUND_ROBIN') {
      if (a.roundRobinCount !== b.roundRobinCount) return a.roundRobinCount - b.roundRobinCount;
      if (a.weeklyShifts !== b.weeklyShifts) return a.weeklyShifts - b.weeklyShifts;
    }
    return String(a.employeeId).localeCompare(String(b.employeeId));
  });

  return scored[0].employeeId;
};

const parseApiErrorFromBodyText = (text) => {
  if (!text || typeof text !== 'string') return '';
  try {
    const j = JSON.parse(text);
    if (j && typeof j.error === 'string') return j.error;
  } catch (_) {
    /* plain text body */
  }
  return text;
};

const formatEmployeeName = (emp) => {
  if (!emp) return '';
  if (emp.fullName && emp.fullName.trim()) return emp.fullName.trim();
  const first = emp.firstName?.trim() || '';
  const last = emp.lastName?.trim() || '';
  const full = `${first} ${last}`.trim();
  return full || emp.employeeCode || String(emp.employeeId || '').slice(0, 8);
};

const getCoverageVisual = (assignedCount, requiredCount) => {
  const required = Math.max(Number(requiredCount) || 0, 1);
  const assigned = Number(assignedCount) || 0;
  if (assigned <= 0) {
    return { label: 'Not Assigned', bg: '#FFEBEE', color: '#C62828' };
  }
  if (assigned >= required) {
    return { label: 'Full', bg: '#E8F5E9', color: '#2E7D32' };
  }
  return { label: 'Partial', bg: '#FFF8E1', color: '#EF6C00' };
};

/** `roleSummary`: day-view role row aggregate — dashed border, pattern, icon (vs employee rows). */
const ScheduleShiftCardShell = ({
  entry,
  cv,
  assignedNames,
  leaveConflictNames = [],
  paperSx,
  onOpenStaffDetail,
  children,
  variant = 'resource',
}) => {
  const isRoleSummary = variant === 'roleSummary';
  const tooltipTitle = (
    <Box>
      {isRoleSummary ? (
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
          Position coverage (all shifts this day)
        </Typography>
      ) : null}
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
        Assigned staff
      </Typography>
      {assignedNames.length ? (
        assignedNames.map((name, i) => (
          <Typography key={`${name}-${i}`} variant="caption" sx={{ display: 'block' }}>
            {name}
          </Typography>
        ))
      ) : (
        <Typography variant="caption" sx={{ display: 'block' }}>
          No employees assigned
        </Typography>
      )}
      {leaveConflictNames.length ? (
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: '#FFCDD2', mt: 0.5 }}>
          On leave: {leaveConflictNames.join(', ')} — rescheduling required
        </Typography>
      ) : null}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.85 }}>
        Click for full details
      </Typography>
    </Box>
  );

  return (
    <Tooltip
      title={tooltipTitle}
      arrow
      placement="top"
      enterTouchDelay={450}
      componentsProps={{ tooltip: { sx: { maxWidth: 320 } } }}
    >
      <Paper
        variant="outlined"
        elevation={0}
        onClick={(e) => {
          e.stopPropagation();
          onOpenStaffDetail(e.currentTarget, entry, assignedNames, cv);
        }}
        sx={{
          ...paperSx,
          cursor: 'pointer',
          ...(isRoleSummary && {
            bgcolor: ROLE_SUMMARY_SHIFT_FILL,
            border: '1px dashed rgba(55, 78, 120, 0.38)',
            borderLeft: `3px solid ${cv.color}`,
            backgroundImage:
              'repeating-linear-gradient(135deg, rgba(55,78,120,0.09) 0px, rgba(55,78,120,0.09) 2px, transparent 2px, transparent 7px)',
          }),
        }}
      >
        {isRoleSummary ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.35, minWidth: 0 }}>
            <LayersOutlinedIcon
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                opacity: 0.9,
                flexShrink: 0,
                mt: 0.1,
              }}
              aria-hidden
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
          </Box>
        ) : (
          children
        )}
      </Paper>
    </Tooltip>
  );
};

/** Pixels per hour in weekly (non-fluid) timeline; larger = wider columns, less cramped. */
const WEEKLY_TIMELINE_HOUR_WIDTH = 40;
const WEEKLY_TIMELINE_LEFT_COL_WIDTH = 220;
/** Taller rows so weekly/day shift pills are less cramped. */
const WEEKLY_TIMELINE_ROW_HEIGHT = 60;
const TIMELINE_MINUTES_PER_DAY = 24 * 60;
const TIMELINE_POSITION_FREEZE_STORAGE_KEY = 'workforce-scheduler-freeze-positions';
/** Opaque tints so a frozen left column does not show timeline bars through it. */
const TIMELINE_STICKY_HEADER_BG = '#F0F6F1';
const TIMELINE_STICKY_WEEKLY_ROLE_BG = '#F5F5F5';

const readStoredPositionColumnFrozen = () => {
  try {
    const stored = window.localStorage.getItem(TIMELINE_POSITION_FREEZE_STORAGE_KEY);
    if (stored === '0') return false;
    if (stored === '1') return true;
  } catch {
    /* ignore quota / private mode */
  }
  return true;
};

const persistPositionColumnFrozen = (frozen) => {
  try {
    window.localStorage.setItem(TIMELINE_POSITION_FREEZE_STORAGE_KEY, frozen ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
};

const getTimelineLeftColFreezeSx = ({ frozen, bgcolor, isCorner = false }) => {
  if (!frozen) return {};
  return {
    position: 'sticky',
    left: 0,
    zIndex: isCorner ? 4 : 2,
    bgcolor,
    boxShadow: '2px 0 6px rgba(0, 0, 0, 0.12)',
  };
};

/** Day view: fixed label column + fluid timeline (reliable vs flex + width:100%). */
const timelineFluidDayGridTemplate = `${WEEKLY_TIMELINE_LEFT_COL_WIDTH}px minmax(0, 1fr)`;

const parseTimeInputToMinutes = (value) => {
  if (!value || typeof value !== 'string') return 0;
  const [h, m] = value.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(h)) return 0;
  const mm = Number.isNaN(m) ? 0 : m;
  return Math.min(TIMELINE_MINUTES_PER_DAY, Math.max(0, h * 60 + mm));
};

/**
 * Positions a shift segment within a visible [startMin, endMin) window.
 * Returns null if the segment does not intersect the window.
 */
const getShiftSegmentLayout = (segStartMin, segEndMin, useFluidDayWidth, visibleRange) => {
  const vs = visibleRange.startMin;
  const ve = visibleRange.endMin;
  const span = Math.max(ve - vs, 1);
  const a = Math.max(segStartMin, vs);
  const b = Math.min(segEndMin, ve);
  if (b <= a) return null;
  if (useFluidDayWidth) {
    return {
      left: `${((a - vs) / span) * 100}%`,
      width: `${Math.max(((b - a) / span) * 100, 0.35)}%`,
    };
  }
  const pxPerMin = WEEKLY_TIMELINE_HOUR_WIDTH / 60;
  return {
    left: (a - vs) * pxPerMin,
    width: Math.max((b - a) * pxPerMin, 4),
  };
};

/** Hour start minutes for column headers and grid lines (one tick per hour inside the window). */
const buildTimelineHourTicks = (startMin, endMin) => {
  const ticks = [];
  const first = Math.floor(startMin / 60) * 60;
  for (let m = first; m < endMin; m += 60) {
    if (m >= startMin) ticks.push(m);
  }
  if (!ticks.length) ticks.push(startMin);
  return ticks;
};
/** Visible vertical line between Mon | Tue | Wed … (not between hours). */
const weeklyTimelineDayColumnSx = (dayIdx, brandBorder) => ({
  borderRight: `1px solid ${brandBorder}`,
  borderLeft: dayIdx === 0 ? 'none' : '2px solid rgba(0, 0, 0, 0.2)',
  boxSizing: 'border-box',
});

/** 0–23 → 12-hour clock parts for narrow timeline headers. */
const formatTimelineHourParts = (hour24) => {
  const h = ((Math.floor(Number(hour24)) % 24) + 24) % 24;
  const isPm = h >= 12;
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return { hour12, suffix: isPm ? 'PM' : 'AM' };
};

const addDaysToIsoDate = (isoDate, deltaDays) => {
  const d = parseIsoDate(isoDate);
  d.setDate(d.getDate() + deltaDays);
  return toIsoDate(d);
};

/** One segment per calendar day; overnight shifts become two segments (e.g. Mon 23:00–24:00 + Tue 00:00–07:30). */
const splitShiftIntoDaySegments = (businessDate, startTime, endTime) => {
  const sk = toTimeKey(startTime);
  const ek = toTimeKey(endTime);
  const sm = flexibleTimeToMinutes(sk);
  const em = flexibleTimeToMinutes(ek);
  if (sm == null || em == null) return [];
  const overnight = em <= sm;
  if (!overnight) {
    if (em <= sm) return [];
    return [{ dateIso: businessDate, startMin: sm, endMin: em }];
  }
  return [
    { dateIso: businessDate, startMin: sm, endMin: 24 * 60 },
    { dateIso: addDaysToIsoDate(businessDate, 1), startMin: 0, endMin: em },
  ];
};

const Schedule = ({ selectedBusinessUnit, onBack, dataRefreshKey = 0 }) => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFORCE_OPTIMIZATION);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const tenantId = localStorage.getItem('tenant_id') || '';
  const token = localStorage.getItem('token') || '';
  const backendBase = () => process.env.REACT_APP_BACKEND_URL || '';
  const [viewMode, setViewMode] = useState('weekly');
  const [weekStartDay, setWeekStartDay] = useState(DEFAULT_WEEK_START_DAY);
  const [weekEndDay, setWeekEndDay] = useState(DEFAULT_WEEK_END_DAY);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [allCalendarEntries, setAllCalendarEntries] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [weekAnchorDate, setWeekAnchorDate] = useState(() =>
    toIsoDate(getWeekStartDate(new Date(), DEFAULT_WEEK_START_DAY))
  );
  const [selectedDayDate, setSelectedDayDate] = useState(toIsoDate(getMondayFromIsoWeek(getIsoWeekString(new Date()))));
  const [selectedMonth, setSelectedMonth] = useState(toIsoMonth(new Date()));
  /** Visible hours on day/weekly timeline; defaults to full day (0–1440). */
  const [timelineFullDay, setTimelineFullDay] = useState(true);
  const [timelineRangeStart, setTimelineRangeStart] = useState('08:00');
  const [timelineRangeEnd, setTimelineRangeEnd] = useState('17:00');
  const [positionColumnFrozen, setPositionColumnFrozen] = useState(readStoredPositionColumnFrozen);
  const [importNotes, setImportNotes] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [loadingAssignData, setLoadingAssignData] = useState(false);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [assignmentRows, setAssignmentRows] = useState([]);
  const [assignmentRowErrors, setAssignmentRowErrors] = useState({});
  const [employeesById, setEmployeesById] = useState({});
  const [eligibleEmployeeIdsByRole, setEligibleEmployeeIdsByRole] = useState({});
  const [monthDayPopup, setMonthDayPopup] = useState({
    open: false,
    dateLabel: '',
    entries: [],
  });
  const [assignmentCountBySlotKey, setAssignmentCountBySlotKey] = useState({});
  const [assignmentEmployeeIdsBySlotKey, setAssignmentEmployeeIdsBySlotKey] = useState({});
  const [calendarEmployeesById, setCalendarEmployeesById] = useState({});
  const [staffDetailPopover, setStaffDetailPopover] = useState(null);
  /** Day view: role row info icon → full shift summary for that role/day. */
  const [roleCoverageSummaryPopover, setRoleCoverageSummaryPopover] = useState(null);
  const [assignRolePositions, setAssignRolePositions] = useState([]);
  const [employeeLeaveKeys, setEmployeeLeaveKeys] = useState(() => new Set());
  const [schedulingOvertimeByRole, setSchedulingOvertimeByRole] = useState({});
  const [autoAssignPolicy, setAutoAssignPolicy] = useState(() => ({ ...LEGACY_AUTO_ASSIGN_POLICY }));
  const [schedulingApprovalContext, setSchedulingApprovalContext] = useState({
    isApprover: false,
    approverRoleName: 'GENERAL MANAGER',
  });
  const [calendarWeekAssignments, setCalendarWeekAssignments] = useState([]);
  const [assignDialogWeekAssignments, setAssignDialogWeekAssignments] = useState([]);
  const [addShiftDialogOpen, setAddShiftDialogOpen] = useState(false);
  const [addShiftSaving, setAddShiftSaving] = useState(false);
  const [addShiftForm, setAddShiftForm] = useState({
    dayIndex: 0,
    rolePositionId: '',
    startTime: '09:00',
    endTime: '17:00',
    requiredCount: 1,
    shiftLabel: 'Shift',
  });
  const [assignmentDeleteDialogOpen, setAssignmentDeleteDialogOpen] = useState(false);
  const [rowPendingAssignmentDelete, setRowPendingAssignmentDelete] = useState(null);
  const [assignmentDeleteLoading, setAssignmentDeleteLoading] = useState(false);
  const [scheduleSlotDeleteDialogOpen, setScheduleSlotDeleteDialogOpen] = useState(false);
  const [entryPendingScheduleDelete, setEntryPendingScheduleDelete] = useState(null);
  const [scheduleSlotDeleteLoading, setScheduleSlotDeleteLoading] = useState(false);
  const [clearScheduleDialogOpen, setClearScheduleDialogOpen] = useState(false);
  const [clearScheduleLoading, setClearScheduleLoading] = useState(false);
  const [fetchingRolesForAddShift, setFetchingRolesForAddShift] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishBatchId, setPublishBatchId] = useState(null);
  const [publishBatchRows, setPublishBatchRows] = useState([]);
  const [publishActionKey, setPublishActionKey] = useState(null);
  const [sendEmailOnPublish, setSendEmailOnPublish] = useState(false);
  const [employeeCoverageDialogOpen, setEmployeeCoverageDialogOpen] = useState(false);
  const [coverageDataRefreshKey, setCoverageDataRefreshKey] = useState(0);
  const bumpCoverageDataRefresh = () => setCoverageDataRefreshKey((key) => key + 1);
  /** Weekly Gantt: role keys in this set have employee/coverage rows hidden (Outlook-style accordion). */
  const [weeklyTimelineCollapsedRoleKeys, setWeeklyTimelineCollapsedRoleKeys] = useState(() => new Set());
  /** Day Gantt: same collapse behavior for day view. */
  const [dayTimelineCollapsedRoleKeys, setDayTimelineCollapsedRoleKeys] = useState(() => new Set());
  /** Bumps when schedule/assignments reload so week summary grids refresh scheduled labor. */
  const [weekSummaryRefreshKey, setWeekSummaryRefreshKey] = useState(0);

  const { propertyId, label: businessUnitLabel } = resolveWorkforceScope(selectedBusinessUnit);

  useEffect(() => {
    let cancelled = false;
    const loadWorkWeek = async () => {
      if (!tenantId) return;
      try {
        const week = await fetchTenantWorkWeek(backendBase(), token, tenantId);
        if (!cancelled) {
          setWeekStartDay(week.weekStartDay);
          setWeekEndDay(week.weekEndDay);
          setWeekAnchorDate((prev) =>
            toIsoDate(getWeekStartDate(parseIsoDate(prev), week.weekStartDay))
          );
        }
      } catch {
        if (!cancelled) {
          setWeekStartDay(DEFAULT_WEEK_START_DAY);
          setWeekEndDay(DEFAULT_WEEK_END_DAY);
        }
      }
    };
    loadWorkWeek();
    return () => {
      cancelled = true;
    };
  }, [tenantId, token]);

  const workWeekDays = useMemo(
    () => buildWorkWeekDays(weekStartDay, weekEndDay),
    [weekStartDay, weekEndDay]
  );
  const weekDayLabels = workWeekDays.dayLabels;
  const weekDayKeys = workWeekDays.dayKeys;
  const monthDayLabels = useMemo(() => rotateWeekDays(weekStartDay).dayLabels, [weekStartDay]);

  useEffect(() => {
    setPublishBatchId(null);
    setPublishBatchRows([]);
  }, [tenantId, propertyId]);

  useEffect(() => {
    if (!addDialogOpen) return;
    setImportNotes('');
  }, [weekAnchorDate, addDialogOpen]);

  useEffect(() => {
    setWeeklyTimelineCollapsedRoleKeys(new Set());
  }, [weekAnchorDate]);

  useEffect(() => {
    setDayTimelineCollapsedRoleKeys(new Set());
  }, [selectedDayDate]);

  const weekStartDate = weekAnchorDate;
  const selectedWeek = useMemo(
    () => getIsoWeekString(parseIsoDate(weekAnchorDate)),
    [weekAnchorDate]
  );

  const weekDates = useMemo(() => {
    const start = new Date(`${weekStartDate}T00:00:00`);
    return weekDayKeys.map((_, idx) => {
      const d = new Date(start);
      d.setDate(start.getDate() + idx);
      return d;
    });
  }, [weekDayKeys, weekStartDate]);

  const weekRangeLabel = useMemo(() => {
    if (!weekDates.length) return '';
    return `${formatDisplayDate(weekDates[0])} - ${formatDisplayDate(weekDates[weekDates.length - 1])}`;
  }, [weekDates]);

  const weekEndDate = useMemo(
    () => (weekDates.length ? toIsoDate(weekDates[weekDates.length - 1]) : weekStartDate),
    [weekDates, weekStartDate]
  );

  const employeeMaxHoursStatusById = useMemo(() => {
    const totalsByEmployee = {};
    (calendarWeekAssignments || []).forEach((assignment) => {
      if (!assignment?.employeeId) return;
      const status = normalizeDay(assignment.assignmentStatus);
      if (status === 'CANCELLED' || status === 'VOID') return;
      const hours = hoursFromAssignmentRecord(assignment);
      totalsByEmployee[assignment.employeeId] = (totalsByEmployee[assignment.employeeId] || 0) + hours;
    });

    const statusById = {};
    Object.entries(totalsByEmployee).forEach(([employeeId, totalRaw]) => {
      const total = Math.round(totalRaw * 100) / 100;
      const employee = calendarEmployeesById[employeeId];
      if (isEmployeeOvertimeAllowed(employee)) return;
      const maxHours = employee?.maxHoursPerWeek ?? employee?.max_hours_per_week;
      if (maxHours == null || Number(maxHours) <= 0) return;
      const max = Number(maxHours);
      if (total > max + HOURS_EPSILON) {
        statusById[employeeId] = { status: 'over', projectedHours: total, maxHours: max };
      } else if (total >= max - HOURS_EPSILON) {
        statusById[employeeId] = { status: 'warning', projectedHours: total, maxHours: max };
      }
    });
    return statusById;
  }, [calendarWeekAssignments, calendarEmployeesById]);

  const clearScheduleRangeLabel = useMemo(() => {
    if (viewMode === 'day') {
      return formatDisplayDate(parseIsoDate(selectedDayDate));
    }
    return weekRangeLabel || `${formatDisplayDate(weekDates[0])} - ${formatDisplayDate(weekDates[weekDates.length - 1])}`;
  }, [selectedDayDate, viewMode, weekDates, weekRangeLabel]);

  const clearScheduleFromDate = viewMode === 'day' ? selectedDayDate : weekStartDate;
  const clearScheduleToDate = viewMode === 'day' ? selectedDayDate : weekEndDate;

  const weekCalendarEntries = useMemo(() => {
    return allCalendarEntries.filter((row) => row.businessDate >= weekStartDate && row.businessDate <= weekEndDate);
  }, [allCalendarEntries, weekEndDate, weekStartDate]);

  const dayCalendarEntries = useMemo(() => {
    return allCalendarEntries
      .filter((row) => row.businessDate === selectedDayDate)
      .sort((a, b) => `${a.startTime}`.localeCompare(`${b.startTime}`));
  }, [allCalendarEntries, selectedDayDate]);

  const monthAnchorDate = useMemo(() => parseIsoDate(`${selectedMonth}-01`), [selectedMonth]);
  const monthLabel = useMemo(
    () => monthAnchorDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    [monthAnchorDate]
  );

  const monthlyCalendar = useMemo(() => {
    const firstOfMonth = new Date(monthAnchorDate.getFullYear(), monthAnchorDate.getMonth(), 1);
    const lastOfMonth = new Date(monthAnchorDate.getFullYear(), monthAnchorDate.getMonth() + 1, 0);
    const startOffset = getMonthGridStartOffset(firstOfMonth, weekStartDay);
    const daysInMonth = lastOfMonth.getDate();

    const entriesByDate = new Map();
    allCalendarEntries.forEach((entry) => {
      const list = entriesByDate.get(entry.businessDate) || [];
      list.push(entry);
      entriesByDate.set(entry.businessDate, list);
    });

    const cells = [];
    for (let i = 0; i < startOffset; i += 1) {
      cells.push({ key: `empty-start-${i}`, empty: true });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      const d = new Date(monthAnchorDate.getFullYear(), monthAnchorDate.getMonth(), day);
      const iso = toIsoDate(d);
      cells.push({
        key: iso,
        empty: false,
        date: iso,
        dayNumber: day,
        entries: (entriesByDate.get(iso) || []).sort((a, b) => `${a.startTime}`.localeCompare(`${b.startTime}`)),
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ key: `empty-end-${cells.length}`, empty: true });
    }
    return cells;
  }, [allCalendarEntries, monthAnchorDate, weekStartDay]);

  const getScheduleEntryCoverageVisual = (entry) => {
    const key = buildSlotKey(entry);
    const required = Math.max(Number(entry.requiredCount) || 0, 1);
    const assigned = assignmentCountBySlotKey[key] || 0;
    return { ...getCoverageVisual(assigned, required), assigned, required };
  };

  const getAssignedEmployeeNamesForEntry = (entry) => {
    const key = buildSlotKey(entry);
    const ids = assignmentEmployeeIdsBySlotKey[key] || [];
    return ids.map((id) => {
      const label = formatEmployeeName(calendarEmployeesById[id]);
      return label || `Employee ${String(id).replace(/-/g, '').slice(0, 8)}…`;
    });
  };

  const weekIsoDates = useMemo(() => weekDates.map((d) => toIsoDate(d)), [weekDates]);

  const weekTimelineRoleGroups = useMemo(() => {
    const isoSet = new Set(weekIsoDates);
    if (!weekCalendarEntries.length) return [];

    const coverageVisualForEntry = (entry) => {
      const key = buildSlotKey(entry);
      const required = Math.max(Number(entry.requiredCount) || 0, 1);
      const assigned = assignmentCountBySlotKey[key] || 0;
      return { ...getCoverageVisual(assigned, required), assigned, required };
    };

    const byRole = new Map();
    weekCalendarEntries.forEach((entry) => {
      const rid = entry.rolePositionId ?? '';
      if (!byRole.has(rid)) {
        byRole.set(rid, { roleLabel: entry.roleLabel || 'Position', entries: [] });
      }
      byRole.get(rid).entries.push(entry);
    });

    const groups = [];
    byRole.forEach((data, rolePositionId) => {
      const entries = [...data.entries].sort((a, b) =>
        `${a.businessDate}|${toTimeKey(a.startTime)}`.localeCompare(`${b.businessDate}|${toTimeKey(b.startTime)}`)
      );

      const employeeIdSet = new Set();
      entries.forEach((entry) => {
        const key = buildSlotKey(entry);
        (assignmentEmployeeIdsBySlotKey[key] || []).forEach((id) => {
          if (id) employeeIdSet.add(id);
        });
      });

      const employeeRows = Array.from(employeeIdSet)
        .map((employeeId) => {
          const blocks = [];
          entries.forEach((entry) => {
            const key = buildSlotKey(entry);
            if (!(assignmentEmployeeIdsBySlotKey[key] || []).includes(employeeId)) return;
            const cv = coverageVisualForEntry(entry);
            const segments = splitShiftIntoDaySegments(entry.businessDate, entry.startTime, entry.endTime).filter((seg) =>
              isoSet.has(seg.dateIso)
            );
            if (segments.length) {
              blocks.push({ entry, segments, cv });
            }
          });
          return {
            employeeId,
            name:
              formatEmployeeName(calendarEmployeesById[employeeId]) ||
              `Employee ${String(employeeId).replace(/-/g, '').slice(0, 8)}`,
            blocks,
          };
        })
        .filter((row) => row.blocks.length)
        .sort((a, b) => a.name.localeCompare(b.name));

      const unassignedBlocks = [];
      entries.forEach((entry) => {
        const cv = coverageVisualForEntry(entry);
        if (cv.assigned > 0) return;
        const segments = splitShiftIntoDaySegments(entry.businessDate, entry.startTime, entry.endTime).filter((seg) =>
          isoSet.has(seg.dateIso)
        );
        if (segments.length) {
          unassignedBlocks.push({ entry, segments, cv });
        }
      });

      const weeklyAggregatedBlocks = [];
      entries.forEach((entry) => {
        const cv = coverageVisualForEntry(entry);
        const segments = splitShiftIntoDaySegments(entry.businessDate, entry.startTime, entry.endTime).filter((seg) =>
          isoSet.has(seg.dateIso)
        );
        if (segments.length) {
          weeklyAggregatedBlocks.push({ entry, segments, cv });
        }
      });

      if (!employeeRows.length && !unassignedBlocks.length) return;

      groups.push({
        rolePositionId,
        roleLabel: data.roleLabel,
        employeeRows,
        weeklyAggregatedBlocks,
        unassignedBlocks,
        coverageSummary: {
          shiftSlots: entries.length,
          scheduleSummaryByDay: Object.fromEntries(
            weekIsoDates.map((iso) => {
              const dayEntries = entries.filter((e) => e.businessDate === iso);
              return [iso, buildRoleScheduleSummary(dayEntries)];
            })
          ),
        },
      });
    });

    return groups.sort((a, b) => a.roleLabel.localeCompare(b.roleLabel));
  }, [weekCalendarEntries, weekIsoDates, assignmentEmployeeIdsBySlotKey, assignmentCountBySlotKey, calendarEmployeesById]);

  const dayTimelineRoleGroups = useMemo(() => {
    const isoSet = new Set([selectedDayDate]);
    if (!dayCalendarEntries.length) return [];

    const coverageVisualForEntry = (entry) => {
      const key = buildSlotKey(entry);
      const required = Math.max(Number(entry.requiredCount) || 0, 1);
      const assigned = assignmentCountBySlotKey[key] || 0;
      return { ...getCoverageVisual(assigned, required), assigned, required };
    };

    const byRole = new Map();
    dayCalendarEntries.forEach((entry) => {
      const rid = entry.rolePositionId ?? '';
      if (!byRole.has(rid)) {
        byRole.set(rid, { roleLabel: entry.roleLabel || 'Position', entries: [] });
      }
      byRole.get(rid).entries.push(entry);
    });

    const groups = [];
    const dayIsoDates = [selectedDayDate];
    byRole.forEach((data, rolePositionId) => {
      const entries = [...data.entries].sort((a, b) =>
        `${a.businessDate}|${toTimeKey(a.startTime)}`.localeCompare(`${b.businessDate}|${toTimeKey(b.startTime)}`)
      );

      const employeeIdSet = new Set();
      entries.forEach((entry) => {
        const key = buildSlotKey(entry);
        (assignmentEmployeeIdsBySlotKey[key] || []).forEach((id) => {
          if (id) employeeIdSet.add(id);
        });
      });

      const employeeRows = Array.from(employeeIdSet)
        .map((employeeId) => {
          const blocks = [];
          entries.forEach((entry) => {
            const key = buildSlotKey(entry);
            if (!(assignmentEmployeeIdsBySlotKey[key] || []).includes(employeeId)) return;
            const cv = coverageVisualForEntry(entry);
            const segments = splitShiftIntoDaySegments(entry.businessDate, entry.startTime, entry.endTime).filter((seg) =>
              isoSet.has(seg.dateIso)
            );
            if (segments.length) {
              blocks.push({ entry, segments, cv });
            }
          });
          return {
            employeeId,
            name:
              formatEmployeeName(calendarEmployeesById[employeeId]) ||
              `Employee ${String(employeeId).replace(/-/g, '').slice(0, 8)}`,
            blocks,
          };
        })
        .filter((row) => row.blocks.length)
        .sort((a, b) => a.name.localeCompare(b.name));

      const unassignedBlocks = [];
      entries.forEach((entry) => {
        const cv = coverageVisualForEntry(entry);
        if (cv.assigned > 0) return;
        const segments = splitShiftIntoDaySegments(entry.businessDate, entry.startTime, entry.endTime).filter((seg) =>
          isoSet.has(seg.dateIso)
        );
        if (segments.length) {
          unassignedBlocks.push({ entry, segments, cv });
        }
      });

      const dayAggregatedBlocks = [];
      entries.forEach((entry) => {
        const cv = coverageVisualForEntry(entry);
        const segments = splitShiftIntoDaySegments(entry.businessDate, entry.startTime, entry.endTime).filter((seg) =>
          isoSet.has(seg.dateIso)
        );
        if (segments.length) {
          dayAggregatedBlocks.push({ entry, segments, cv });
        }
      });

      if (!employeeRows.length && !unassignedBlocks.length) return;

      groups.push({
        rolePositionId,
        roleLabel: data.roleLabel,
        employeeRows,
        dayAggregatedBlocks,
        unassignedBlocks,
        coverageSummary: {
          shiftSlots: entries.length,
          scheduleSummaryByDay: Object.fromEntries(
            dayIsoDates.map((iso) => {
              const dayEntries = entries.filter((e) => e.businessDate === iso);
              return [iso, buildRoleScheduleSummary(dayEntries)];
            })
          ),
        },
      });
    });

    return groups.sort((a, b) => a.roleLabel.localeCompare(b.roleLabel));
  }, [dayCalendarEntries, selectedDayDate, assignmentEmployeeIdsBySlotKey, assignmentCountBySlotKey, calendarEmployeesById]);

  const getLeaveConflictNamesForEntry = useCallback(
    (entry) => {
      const ids = getLeaveConflictEmployeeIdsForEntry(entry, assignmentEmployeeIdsBySlotKey, employeeLeaveKeys);
      return ids.map((id) => formatEmployeeName(calendarEmployeesById[id]) || 'Employee');
    },
    [assignmentEmployeeIdsBySlotKey, employeeLeaveKeys, calendarEmployeesById]
  );

  const visibleCalendarEntriesForLeaveCheck = useMemo(() => {
    if (viewMode === 'day') return dayCalendarEntries;
    if (viewMode === 'monthly') {
      const first = toIsoDate(new Date(monthAnchorDate.getFullYear(), monthAnchorDate.getMonth(), 1));
      const last = toIsoDate(new Date(monthAnchorDate.getFullYear(), monthAnchorDate.getMonth() + 1, 0));
      return allCalendarEntries.filter((row) => row.businessDate >= first && row.businessDate <= last);
    }
    return weekCalendarEntries;
  }, [allCalendarEntries, dayCalendarEntries, monthAnchorDate, viewMode, weekCalendarEntries]);

  const scheduleLeaveConflicts = useMemo(() => {
    if (!employeeLeaveKeys.size) return [];
    const conflicts = [];
    const seen = new Set();
    visibleCalendarEntriesForLeaveCheck.forEach((entry) => {
      const dateIso = normalizeApiDateToIso(entry.businessDate);
      const onLeaveIds = getLeaveConflictEmployeeIdsForEntry(
        entry,
        assignmentEmployeeIdsBySlotKey,
        employeeLeaveKeys
      );
      onLeaveIds.forEach((employeeId) => {
        const dedupeKey = `${employeeId}|${dateIso}|${buildSlotKey(entry)}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        conflicts.push({
          employeeId,
          employeeName: formatEmployeeName(calendarEmployeesById[employeeId]) || 'Employee',
          businessDate: dateIso,
          roleLabel: entry.roleLabel || 'Position',
          shiftLabel: entry.shiftCode || entry.shiftName || 'Shift',
        });
      });
    });
    return conflicts.sort((a, b) =>
      `${a.businessDate}|${a.employeeName}`.localeCompare(`${b.businessDate}|${b.employeeName}`)
    );
  }, [
    assignmentEmployeeIdsBySlotKey,
    calendarEmployeesById,
    employeeLeaveKeys,
    visibleCalendarEntriesForLeaveCheck,
  ]);

  const getWeeklyTimelineRoleKey = (group) => String(group.rolePositionId) || group.roleLabel;

  const toggleWeeklyTimelineRoleCollapsed = (key) => {
    setWeeklyTimelineCollapsedRoleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const collapsibleWeeklyRoleKeys = useMemo(() => {
    /** Weekly shows one row per role (no per-employee sub-rows); nothing to collapse. */
    return [];
  }, []);

  const allWeeklyCollapsibleRolesCollapsed = useMemo(
    () =>
      collapsibleWeeklyRoleKeys.length > 0 &&
      collapsibleWeeklyRoleKeys.every((k) => weeklyTimelineCollapsedRoleKeys.has(k)),
    [collapsibleWeeklyRoleKeys, weeklyTimelineCollapsedRoleKeys]
  );

  const toggleWeeklyTimelineCollapseAll = useCallback(() => {
    setWeeklyTimelineCollapsedRoleKeys((prev) => {
      const next = new Set(prev);
      if (collapsibleWeeklyRoleKeys.length === 0) return next;
      const everyCollapsed = collapsibleWeeklyRoleKeys.every((k) => next.has(k));
      if (everyCollapsed) {
        collapsibleWeeklyRoleKeys.forEach((k) => next.delete(k));
      } else {
        collapsibleWeeklyRoleKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  }, [collapsibleWeeklyRoleKeys]);

  const toggleDayTimelineRoleCollapsed = (key) => {
    setDayTimelineCollapsedRoleKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const collapsibleDayRoleKeys = useMemo(() => {
    const keys = [];
    dayTimelineRoleGroups.forEach((g) => {
      const subRowCount = g.employeeRows.length + (g.unassignedBlocks.length ? 1 : 0);
      /** Day view: any role with employees or coverage row can collapse (even a single sub-row). */
      if (subRowCount >= 1) keys.push(getWeeklyTimelineRoleKey(g));
    });
    return keys;
  }, [dayTimelineRoleGroups]);

  const allDayCollapsibleRolesCollapsed = useMemo(
    () =>
      collapsibleDayRoleKeys.length > 0 &&
      collapsibleDayRoleKeys.every((k) => dayTimelineCollapsedRoleKeys.has(k)),
    [collapsibleDayRoleKeys, dayTimelineCollapsedRoleKeys]
  );

  const toggleDayTimelineCollapseAll = useCallback(() => {
    setDayTimelineCollapsedRoleKeys((prev) => {
      const next = new Set(prev);
      if (collapsibleDayRoleKeys.length === 0) return next;
      const everyCollapsed = collapsibleDayRoleKeys.every((k) => next.has(k));
      if (everyCollapsed) {
        collapsibleDayRoleKeys.forEach((k) => next.delete(k));
      } else {
        collapsibleDayRoleKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  }, [collapsibleDayRoleKeys]);

  const openStaffDetailPopover = (anchorEl, entry, assignedNames, cv) => {
    setRoleCoverageSummaryPopover(null);
    setStaffDetailPopover({ anchorEl, entry, assignedNames, cv });
  };

  const fetchWorkforceCalendar = async () => {
    if (!tenantId || !propertyId) {
      setAllCalendarEntries([]);
      setAssignmentCountBySlotKey({});
      setAssignmentEmployeeIdsBySlotKey({});
      setCalendarEmployeesById({});
      setCalendarWeekAssignments([]);
      return null;
    }

    try {
      setLoadingCalendar(true);
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const base = process.env.REACT_APP_BACKEND_URL;

      const [scheduleRes, rolesRes, assignmentRes, employeesRes, leavesRes] = await Promise.all([
        fetch(
          `${base}conductor/workforce-schedule/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/employee-assignment/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/employees/by-tenant-and-status?tenant_id=${encodeURIComponent(tenantId)}&status=ACTIVE`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/employee-leaves/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
      ]);

      if (!scheduleRes.ok) {
        throw new Error(`Failed to load workforce schedule: ${scheduleRes.status}`);
      }
      const scheduleData = await scheduleRes.json();
      const scheduleRows = Array.isArray(scheduleData) ? scheduleData : [];

      const rolesData = rolesRes.ok ? await rolesRes.json() : [];
      const workAreaLabels = await fetchWorkAreaLabelMap(base, headers, localStorage.getItem('tenant_id') || '');
      const roleNameById = new Map(
        (Array.isArray(rolesData) ? rolesData : []).map((role) => [
          role.rolePositionId,
          formatPositionLabel(role, workAreaLabels),
        ])
      );

      const employeesData = employeesRes.ok ? await employeesRes.json() : [];
      const leaveList = leavesRes.ok ? await leavesRes.json() : [];
      const leaveRange = getLeaveRangeForView(
        viewMode,
        weekStartDate,
        weekEndDate,
        selectedDayDate,
        monthAnchorDate
      );
      setEmployeeLeaveKeys(buildEmployeeLeaveKeys(leaveList, leaveRange.start, leaveRange.end));
      const empMap = {};
      (Array.isArray(employeesData) ? employeesData : []).forEach((emp) => {
        if (emp?.employeeId != null) {
          empMap[emp.employeeId] = emp;
        }
      });
      setCalendarEmployeesById(empMap);

      const assignmentList = assignmentRes.ok ? await assignmentRes.json() : [];
      const counts = {};
      const idsBySlot = {};
      (Array.isArray(assignmentList) ? assignmentList : []).forEach((a) => {
        if (!a.employeeId) return;
        const key = buildSlotKey(a);
        counts[key] = (counts[key] || 0) + 1;
        if (!idsBySlot[key]) idsBySlot[key] = [];
        idsBySlot[key].push(a.employeeId);
      });
      setAssignmentCountBySlotKey(counts);
      setAssignmentEmployeeIdsBySlotKey(idsBySlot);
      setCalendarWeekAssignments(
        (Array.isArray(assignmentList) ? assignmentList : []).filter(
          (a) => a.businessDate >= weekStartDate && a.businessDate <= weekEndDate
        )
      );

      const mapped = scheduleRows.map((row) => ({
        ...row,
        roleLabel: roleNameById.get(row.rolePositionId) || `Position ${String(row.rolePositionId || '').slice(0, 8)}`,
      }));
      setAllCalendarEntries(mapped);
      setWeekSummaryRefreshKey((k) => k + 1);
      return { mapped, assignmentList };
    } catch (error) {
      showError(error.message || 'Failed to load workforce schedule');
      setAllCalendarEntries([]);
      setAssignmentCountBySlotKey({});
      setAssignmentEmployeeIdsBySlotKey({});
      setCalendarEmployeesById({});
      setCalendarWeekAssignments([]);
      return null;
    } finally {
      setLoadingCalendar(false);
    }
  };

  useEffect(() => {
    fetchWorkforceCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, propertyId, weekStartDate, selectedDayDate, selectedMonth, viewMode, dataRefreshKey]);

  const importRoleCoverage = async () => {
    if (!tenantId || !propertyId) {
      showError('Select a business unit first.');
      return;
    }
    try {
      setImporting(true);
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const base = process.env.REACT_APP_BACKEND_URL;

      const [rolesRes, coverageRes] = await Promise.all([
        fetch(
          `${base}conductor/role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/weekly-role-coverage/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
      ]);

      if (!rolesRes.ok) {
        throw new Error(`Failed to fetch positions: ${rolesRes.status}`);
      }
      if (!coverageRes.ok) {
        throw new Error(`Failed to fetch workforce calendar: ${coverageRes.status}`);
      }

      const rolesData = await rolesRes.json();
      const coverageData = await coverageRes.json();
      const roles = Array.isArray(rolesData) ? rolesData.filter((r) => r.isActive !== false) : [];
      const coverage = Array.isArray(coverageData) ? coverageData : [];

      const coverageByRoleId = new Map();
      coverage.forEach((item) => {
        if (!item?.rolePositionId) return;
        const existing = coverageByRoleId.get(item.rolePositionId) || [];
        existing.push(item);
        coverageByRoleId.set(item.rolePositionId, existing);
      });

      const dayPayloads = [];
      roles.forEach((role) => {
        const weeklyItems = coverageByRoleId.get(role.rolePositionId) || [];
        if (!weeklyItems.length) return;

        weeklyItems.forEach((weekly) => {
          weekDayKeys.forEach((dayPrefix) => {
            const { startTime, endTime, requiredCount } = getDayPayload(weekly, dayPrefix);
            if (!startTime || !endTime) return;

            const dayDate = dateForCoverageDayInWeek(weekStartDate, dayPrefix);
            dayPayloads.push({
              tenantId,
              propertyId,
              scheduleId: null,
              weekStartDate,
              weekEndDate,
              businessDate: toIsoDate(dayDate),
              dayName: dayDate.toLocaleDateString('en-US', { weekday: 'long' }),
              rolePositionId: role.rolePositionId,
              shiftMasterId: weekly.shiftId || null,
              shiftCode: weekly.shiftCode || null,
              shiftName: weekly.shiftName || null,
              startTime,
              endTime,
              requiredCount: Math.max(Number(requiredCount) || 0, 1),
              scheduleStatus: null,
              notes: importNotes.trim() || null,
            });
          });
        });
      });

      if (!dayPayloads.length) {
        showError('No weekly coverage day slots available to import.');
        setImporting(false);
        return;
      }

      const batchScheduleRes = await fetch(`${base}conductor/workforce-schedule/batch`, {
        method: 'POST',
        headers,
        body: JSON.stringify(dayPayloads),
      });
      if (!batchScheduleRes.ok) {
        const text = await batchScheduleRes.text().catch(() => '');
        throw new Error(parseApiErrorFromBodyText(text) || `POST failed: ${batchScheduleRes.status}`);
      }
      const postResults = await batchScheduleRes.json();

      setAddDialogOpen(false);
      setSelectedDayDate(weekStartDate);

      try {
        const batchBody = {
          tenantId,
          propertyId,
          scheduleId: null,
          fromDate: weekStartDate,
          toDate: weekEndDate,
          weekStartDate,
          weekEndDate,
          createdBy: null,
          notes: importNotes.trim() || null,
        };
        const batchResult = await createPublishedScheduleBatch(backendBase(), token, batchBody);
        const batchArr = Array.isArray(batchResult) ? batchResult : [];
        if (batchArr[0]?.publishBatchId) {
          setPublishBatchId(batchArr[0].publishBatchId);
        }
        showSuccess(
          `Imported ${postResults.length} coverage rows to the schedule. Publish draft saved (version ${batchArr[0]?.versionNumber ?? '—'}).`
        );
      } catch (batchErr) {
        showError(
          `Coverage imported, but publish draft failed: ${batchErr.message || batchErr}. Use Publish → Create draft from current week.`
        );
      }

      await fetchWorkforceCalendar();
    } catch (error) {
      showError(error.message || 'Failed to import position coverage');
    } finally {
      setImporting(false);
    }
  };

  const refreshAssignmentGridAfterScheduleChange = async () => {
    const result = await fetchWorkforceCalendar();
    if (!result?.mapped) return;
    let scheduleRows = result.mapped
      .filter((row) => row.businessDate >= weekStartDate && row.businessDate <= weekEndDate)
      .sort((a, b) =>
        `${a.businessDate}|${toTimeKey(a.startTime)}`.localeCompare(`${b.businessDate}|${toTimeKey(b.startTime)}`)
      );
    if (viewMode === 'day') {
      scheduleRows = scheduleRows.filter((row) => row.businessDate === selectedDayDate);
    }
    setAssignmentRows(buildAssignmentRowsFromSchedule(scheduleRows, result.assignmentList || []));
  };

  const openAssignDialog = async () => {
    if (!canEdit) return;
    if (!tenantId || !propertyId) {
      showError('Select a business unit first.');
      return;
    }
    try {
      setLoadingAssignData(true);
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const base = process.env.REACT_APP_BACKEND_URL;

      const [employeesRes, employeePropertyRes, employeeRoleRes, assignmentRes, rolesRes, leavesRes, overtimeContextRes, approvalContextRes, autoAssignPolicyRes] = await Promise.all([
        fetch(`${base}conductor/employees/by-tenant-and-status?tenant_id=${encodeURIComponent(tenantId)}&status=ACTIVE`, { method: 'GET', headers }),
        fetch(`${base}conductor/employee-properties/by-tenant-and-status?tenant_id=${encodeURIComponent(tenantId)}&status=ACTIVE`, { method: 'GET', headers }),
        fetch(`${base}conductor/employee-role-positions/by-tenant-and-status?tenant_id=${encodeURIComponent(tenantId)}&status=ACTIVE`, { method: 'GET', headers }),
        fetch(
          `${base}conductor/employee-assignment/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/employee-leaves/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/workforce-labor-standard/scheduling-overtime-context?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}&week_start_date=${encodeURIComponent(weekStartDate)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/workforce-scheduling/approval-context?tenant_id=${encodeURIComponent(tenantId)}`,
          { method: 'GET', headers }
        ),
        fetch(
          `${base}conductor/workforce-auto-assign-policy?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
          { method: 'GET', headers }
        ),
      ]);

      if (!employeesRes.ok || !employeePropertyRes.ok || !employeeRoleRes.ok || !assignmentRes.ok) {
        throw new Error('Failed to load assignment data');
      }

      const [employees, employeeProperties, employeeRoles, existingAssignments] = await Promise.all([
        employeesRes.json(),
        employeePropertyRes.json(),
        employeeRoleRes.json(),
        assignmentRes.json(),
      ]);
      const rolesData = rolesRes.ok ? await rolesRes.json() : [];
      const leaveList = leavesRes.ok ? await leavesRes.json() : [];
      const overtimeContextPayload = overtimeContextRes.ok ? await overtimeContextRes.json() : {};
      const approvalContextPayload = approvalContextRes.ok ? await approvalContextRes.json() : {};
      const autoAssignPolicyPayload = autoAssignPolicyRes.ok ? await autoAssignPolicyRes.json() : null;
      setAutoAssignPolicy(mapAutoAssignPolicyFromApi(autoAssignPolicyPayload));
      setSchedulingOvertimeByRole(
        overtimeContextPayload.by_role_position_id || overtimeContextPayload.byRolePositionId || {}
      );
      setSchedulingApprovalContext({
        isApprover: Boolean(approvalContextPayload.isApprover ?? approvalContextPayload.is_approver),
        approverRoleName:
          approvalContextPayload.approverRoleName ||
          approvalContextPayload.approver_role_name ||
          'GENERAL MANAGER',
        workflowStepName:
          approvalContextPayload.workflowStepName || approvalContextPayload.workflow_step_name || '',
      });
      const leaveRangeStart = viewMode === 'day' ? selectedDayDate : weekStartDate;
      const leaveRangeEnd = viewMode === 'day' ? selectedDayDate : weekEndDate;
      setEmployeeLeaveKeys(buildEmployeeLeaveKeys(leaveList, leaveRangeStart, leaveRangeEnd));

      const employeeList = Array.isArray(employees) ? employees : [];
      const propertyMappings = (Array.isArray(employeeProperties) ? employeeProperties : []).filter(
        (ep) => ep.propertyId === propertyId && normalizeDay(ep.status) === 'ACTIVE'
      );
      const roleMappings = (Array.isArray(employeeRoles) ? employeeRoles : []).filter((er) => normalizeDay(er.status) === 'ACTIVE');
      const assignmentList = Array.isArray(existingAssignments) ? existingAssignments : [];
      setAssignDialogWeekAssignments(
        assignmentList.filter((a) => a.businessDate >= weekStartDate && a.businessDate <= weekEndDate)
      );
      const roles = (Array.isArray(rolesData) ? rolesData : []).filter((r) => r.isActive !== false);
      const workAreaLabels = await fetchWorkAreaLabelMap(base, headers, localStorage.getItem('tenant_id') || '');
      setAssignRolePositions(
        roles.map((r) => ({
          rolePositionId: r.rolePositionId,
          label: formatPositionLabel(r, workAreaLabels),
        }))
      );

      const employeesByIdMap = {};
      employeeList.forEach((emp) => {
        employeesByIdMap[emp.employeeId] = emp;
      });

      const activePropertyEmployeeIds = new Set(propertyMappings.map((ep) => ep.employeeId));
      const roleEmployeeIds = {};
      roleMappings.forEach((rp) => {
        if (!activePropertyEmployeeIds.has(rp.employeeId)) return;
        const existing = roleEmployeeIds[rp.rolePositionId] || [];
        roleEmployeeIds[rp.rolePositionId] = [...existing, rp.employeeId];
      });

      let scheduleRows = [...weekCalendarEntries].sort((a, b) =>
        `${a.businessDate}|${toTimeKey(a.startTime)}`.localeCompare(`${b.businessDate}|${toTimeKey(b.startTime)}`)
      );
      if (viewMode === 'day') {
        scheduleRows = scheduleRows.filter((row) => row.businessDate === selectedDayDate);
      }

      const rows = buildAssignmentRowsFromSchedule(scheduleRows, assignmentList);

      setEmployeesById(employeesByIdMap);
      setEligibleEmployeeIdsByRole(roleEmployeeIds);
      setAssignmentRows(rows);
      setAssignmentRowErrors({});
      setAssignDialogOpen(true);
    } catch (error) {
      showError(error.message || 'Failed to open assignment modal');
    } finally {
      setLoadingAssignData(false);
    }
  };

  const submitAddShift = async () => {
    if (!tenantId || !propertyId) return;
    if (!addShiftForm.rolePositionId) {
      showError('Select a position.');
      return;
    }
    const start = toTimeKey(addShiftForm.startTime);
    const end = toTimeKey(addShiftForm.endTime);
    if (!start || !end || start === end) {
      showError('Enter a valid shift range (overnight shifts such as 23:00–07:30 are allowed).');
      return;
    }
    try {
      setAddShiftSaving(true);
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const base = process.env.REACT_APP_BACKEND_URL;
      const businessDate = viewMode === 'day'
        ? selectedDayDate
        : toIsoDate(weekDates[addShiftForm.dayIndex] || weekDates[0]);
      const label = addShiftForm.shiftLabel.trim() || 'Manual';
      const payload = {
        tenantId,
        propertyId,
        scheduleId: null,
        weekStartDate,
        weekEndDate,
        businessDate,
        dayName: viewMode === 'day'
          ? parseIsoDate(selectedDayDate).toLocaleDateString('en-US', { weekday: 'long' })
          : weekDayLabels[addShiftForm.dayIndex],
        rolePositionId: addShiftForm.rolePositionId,
        shiftMasterId: null,
        shiftCode: label,
        shiftName: label,
        startTime: start,
        endTime: end,
        requiredCount: Math.max(Number(addShiftForm.requiredCount) || 0, 1),
        scheduleStatus: null,
        notes: assignDialogOpen ? 'Added from Assign' : 'Added from Schedule',
      };
      const postRes = await fetch(`${base}conductor/workforce-schedule`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (!postRes.ok) {
        const text = await postRes.text().catch(() => '');
        throw new Error(text || `POST failed: ${postRes.status}`);
      }
      setAddShiftDialogOpen(false);
      showSuccess(
        viewMode === 'day'
          ? `Shift added for ${formatDisplayDate(parseIsoDate(selectedDayDate))}.`
          : 'Shift added to this week’s schedule.'
      );
      if (assignDialogOpen) {
        await refreshAssignmentGridAfterScheduleChange();
      } else {
        await fetchWorkforceCalendar();
      }
    } catch (error) {
      showError(error.message || 'Failed to add shift');
    } finally {
      setAddShiftSaving(false);
    }
  };

  const openAddShiftFromSchedule = async () => {
    if (!canAdd) return;
    if (!tenantId || !propertyId) {
      showError('Select a business unit first.');
      return;
    }
    try {
      setFetchingRolesForAddShift(true);
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(
        `${base}conductor/role-positions/by-tenant?tenant_id=${encodeURIComponent(tenantId)}&property_id=${encodeURIComponent(propertyId)}`,
        { method: 'GET', headers }
      );
      const data = res.ok ? await res.json() : [];
      const roles = (Array.isArray(data) ? data : []).filter((r) => r.isActive !== false);
      const workAreaLabels = await fetchWorkAreaLabelMap(base, headers, localStorage.getItem('tenant_id') || '');
      setAssignRolePositions(
        roles.map((r) => ({
          rolePositionId: r.rolePositionId,
          label: formatPositionLabel(r, workAreaLabels),
        }))
      );
      let dayIdx = 0;
      if (viewMode === 'day') {
        const d = parseIsoDate(selectedDayDate);
        const mon = parseIsoDate(weekStartDate);
        const diff = Math.round((d.getTime() - mon.getTime()) / 86400000);
        dayIdx = Math.max(0, Math.min(weekDates.length - 1, diff));
      }
      setAddShiftForm((prev) => ({
        ...prev,
        dayIndex: dayIdx,
        rolePositionId: roles[0]?.rolePositionId || '',
      }));
      setAddShiftDialogOpen(true);
    } catch (error) {
      showError(error.message || 'Failed to load positions');
    } finally {
      setFetchingRolesForAddShift(false);
    }
  };

  const openScheduleSlotDeleteDialog = (entry) => {
    setEntryPendingScheduleDelete(entry);
    setScheduleSlotDeleteDialogOpen(true);
  };

  const closeScheduleSlotDeleteDialog = () => {
    if (scheduleSlotDeleteLoading) return;
    setScheduleSlotDeleteDialogOpen(false);
    setEntryPendingScheduleDelete(null);
  };

  const confirmDeleteScheduleSlot = async () => {
    const entry = entryPendingScheduleDelete;
    if (!entry?.coverageId) {
      closeScheduleSlotDeleteDialog();
      return;
    }
    try {
      setScheduleSlotDeleteLoading(true);
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const base = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${base}conductor/workforce-schedule/${encodeURIComponent(entry.coverageId)}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok && res.status !== 204) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Remove failed (${res.status})`);
      }
      setScheduleSlotDeleteDialogOpen(false);
      setEntryPendingScheduleDelete(null);
      setStaffDetailPopover(null);
      setMonthDayPopup((prev) => (prev.open ? { open: false, dateLabel: '', entries: [] } : prev));
      showSuccess('Shift removed from schedule.');
      await fetchWorkforceCalendar();
      bumpCoverageDataRefresh();
      if (assignDialogOpen) {
        await refreshAssignmentGridAfterScheduleChange();
      }
    } catch (error) {
      showError(error.message || 'Failed to remove shift');
    } finally {
      setScheduleSlotDeleteLoading(false);
    }
  };

  const closeClearScheduleDialog = () => {
    if (clearScheduleLoading) return;
    setClearScheduleDialogOpen(false);
  };

  const confirmClearSchedule = async () => {
    if (!canDelete) return;
    if (!tenantId || !propertyId || !clearScheduleFromDate || !clearScheduleToDate) {
      closeClearScheduleDialog();
      return;
    }
    try {
      setClearScheduleLoading(true);
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const base = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams({
        tenant_id: tenantId,
        property_id: propertyId,
        from_date: clearScheduleFromDate,
        to_date: clearScheduleToDate,
      });
      const res = await fetch(`${base}conductor/workforce-schedule/clear?${params.toString()}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Clear schedule failed (${res.status})`);
      }
      const result = await res.json().catch(() => ({}));
      setClearScheduleDialogOpen(false);
      setStaffDetailPopover(null);
      setMonthDayPopup((prev) => (prev.open ? { open: false, dateLabel: '', entries: [] } : prev));
      if (assignDialogOpen) {
        setAssignDialogOpen(false);
        setAssignmentRows([]);
      }
      const shiftCount = result.deletedShiftCount ?? 0;
      const assignmentCount = result.deletedAssignmentCount ?? 0;
      showSuccess(
        `Schedule cleared for ${clearScheduleRangeLabel}. Removed ${shiftCount} shift${shiftCount === 1 ? '' : 's'} and ${assignmentCount} assignment${assignmentCount === 1 ? '' : 's'}.`
      );
      await fetchWorkforceCalendar();
      bumpCoverageDataRefresh();
    } catch (error) {
      showError(error.message || 'Failed to clear schedule');
    } finally {
      setClearScheduleLoading(false);
    }
  };

  const executeRemoveAssignment = async (row) => {
    if (!row.employeeAssignmentId) {
      clearAssignmentRowError(row.rowKey);
      setAssignmentRows((prev) =>
        prev.map((item) =>
          item.rowKey === row.rowKey ? { ...item, employeeId: '', assignmentType: 'MANUAL' } : item
        )
      );
      return;
    }
    const headers = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    };
    const base = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(
      `${base}conductor/employee-assignment/${encodeURIComponent(row.employeeAssignmentId)}`,
      { method: 'DELETE', headers }
    );
    if (!res.ok && res.status !== 204) {
      const text = await res.text().catch(() => '');
      throw new Error(text || `Delete failed (${res.status})`);
    }
    clearAssignmentRowError(row.rowKey);
    setAssignmentRows((prev) =>
      prev.map((item) =>
        item.rowKey === row.rowKey
          ? { ...item, employeeId: '', employeeAssignmentId: null, assignmentType: 'MANUAL' }
          : item
      )
    );
    await fetchWorkforceCalendar();
    showSuccess('Assignment removed.');
  };

  const openAssignmentDeleteDialog = (row) => {
    setRowPendingAssignmentDelete(row);
    setAssignmentDeleteDialogOpen(true);
  };

  const closeAssignmentDeleteDialog = () => {
    if (assignmentDeleteLoading) return;
    setAssignmentDeleteDialogOpen(false);
    setRowPendingAssignmentDelete(null);
  };

  const confirmRemoveAssignment = async () => {
    const row = rowPendingAssignmentDelete;
    if (!row) return;
    try {
      setAssignmentDeleteLoading(true);
      await executeRemoveAssignment(row);
      setAssignmentDeleteDialogOpen(false);
      setRowPendingAssignmentDelete(null);
    } catch (error) {
      showError(error.message || 'Failed to remove assignment');
    } finally {
      setAssignmentDeleteLoading(false);
    }
  };

  const canAssignEmployeeToRow = (employeeId, row, workingRows) => {
    return !getAssignmentValidationMessage(row, employeeId, workingRows);
  };

  const getAssignmentValidationMessage = (row, employeeId, workingRows, approvalContext = schedulingApprovalContext) => {
    if (!employeeId) return null;
    const employee = employeesById[employeeId];
    if (!employee) return 'Cannot assign: employee record not found.';

    const eligibleForRole = (eligibleEmployeeIdsByRole[row.rolePositionId] || []).includes(employeeId);
    if (!eligibleForRole) {
      return 'Cannot assign this employee: not mapped to this position for this property.';
    }

    const leaveKey = `${employeeId}|${row.businessDate}`;
    if (employeeLeaveKeys.has(leaveKey)) {
      return 'Cannot assign this employee: they are on leave on this date.';
    }

    if (!isEmployeeAvailableForDate(employee.availability, row.businessDate, row.startTime, row.endTime)) {
      return 'Cannot assign this employee: not available for this shift (calendar or preferred hours).';
    }

    const hasOverlap = workingRows.some(
      (other) =>
        other.rowKey !== row.rowKey &&
        other.employeeId === employeeId &&
        other.businessDate === row.businessDate &&
        isTimeOverlap(row.businessDate, other.startTime, other.endTime, row.startTime, row.endTime)
    );
    if (hasOverlap) {
      return 'Cannot assign this employee: they already have another assignment that overlaps this shift.';
    }

    const projectedWeeklyHours = computeEmployeeWeeklyHoursForProposal(
      employeeId,
      row,
      workingRows,
      assignDialogWeekAssignments,
      weekStartDay
    );
    const maxHours = employee.maxHoursPerWeek ?? employee.max_hours_per_week;
    const maxHoursStatus = getMaxHoursStatus(
      employeeId,
      row,
      workingRows,
      assignDialogWeekAssignments,
      employeesById,
      weekStartDay
    );
    if (maxHoursStatus.status === 'over') {
      if (approvalContext?.isApprover && row.gmOverrideApproved) {
        // GM approved override — allowed
      } else if (approvalContext?.isApprover) {
        return `Exceeds max weekly hours (${maxHoursStatus.maxHours}). Check "GM approve override" before saving.`;
      } else {
        return `Cannot assign this employee: would exceed maximum weekly hours (${maxHours}). GM approval required.`;
      }
    }

    const isSalaried = employee.isSalaried === true || employee.is_salaried === 'YES' || employee.is_salaried === true;
    const overtimeAllowed = isSalaried
      ? false
      : (employee.isOvertimeAllowed ?? employee.is_overtime_allowed);
    if (overtimeAllowed === false) {
      const roleOt = schedulingOvertimeByRole[row.rolePositionId] || {};
      const weeklyLimit =
        roleOt.weekly_overtime_threshold ?? roleOt.weeklyOvertimeThreshold ?? null;
      if (weeklyLimit != null && Number(weeklyLimit) > 0 && projectedWeeklyHours > Number(weeklyLimit) + 1e-6) {
        return `Cannot assign this employee: not eligible for overtime (limit ${weeklyLimit} hours per week from property overtime rules).`;
      }

      const dailyLimit = roleOt.daily_overtime_threshold ?? roleOt.dailyOvertimeThreshold ?? null;
      if (dailyLimit != null && Number(dailyLimit) > 0) {
        const projectedDailyHours = computeEmployeeDailyHoursForProposal(
          employeeId,
          row,
          workingRows,
          assignDialogWeekAssignments
        );
        if (projectedDailyHours > Number(dailyLimit) + 1e-6) {
          return `Cannot assign this employee: not eligible for overtime (limit ${dailyLimit} hours per day from property overtime rules).`;
        }
      }
    }

    return null;
  };

  const autoAssignEmployees = () => {
    setAssignmentRows((prevRows) => {
      const nextRows = [...prevRows];
      const passAssignCounts = {};
      for (let i = 0; i < nextRows.length; i += 1) {
        const row = nextRows[i];
        if (row.employeeId) continue;
        const candidates = (eligibleEmployeeIdsByRole[row.rolePositionId] || []).filter(
          (employeeId) =>
            canAssignEmployeeToRow(employeeId, row, nextRows) &&
            passesAutoAssignPolicyConstraints(
              employeeId,
              row,
              nextRows,
              assignDialogWeekAssignments,
              autoAssignPolicy
            )
        );
        if (!candidates.length) continue;
        const selectedId = pickAutoAssignEmployee(
          candidates,
          row,
          nextRows,
          assignDialogWeekAssignments,
          employeesById,
          autoAssignPolicy,
          passAssignCounts,
          weekStartDay
        );
        if (!selectedId) continue;
        passAssignCounts[selectedId] = (passAssignCounts[selectedId] || 0) + 1;
        nextRows[i] = {
          ...row,
          employeeId: selectedId,
          assignmentType: 'AUTO',
          assignmentStatus: row.assignmentStatus || 'ASSIGNED',
        };
      }
      return nextRows;
    });
  };

  const updateAssignmentRow = (rowKey, field, value) => {
    setAssignmentRows((prev) => prev.map((row) => (row.rowKey === rowKey ? { ...row, [field]: value } : row)));
    if (field === 'gmOverrideApproved' && value) {
      setAssignmentRowErrors((prev) => {
        if (!prev[rowKey]) return prev;
        const next = { ...prev };
        delete next[rowKey];
        return next;
      });
    }
  };

  const clearAssignmentRowError = (rowKey) => {
    setAssignmentRowErrors((prev) => {
      if (!prev[rowKey]) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const handleEmployeeSelectionChange = (row, nextEmployeeId) => {
    let validationMessage = null;
    let updatedRows = null;

    setAssignmentRows((prevRows) => {
      const nextRows = prevRows.map((item) =>
        item.rowKey === row.rowKey
          ? {
              ...item,
              employeeId: nextEmployeeId,
              assignmentType: nextEmployeeId ? 'MANUAL' : item.assignmentType,
              gmOverrideApproved: nextEmployeeId ? item.gmOverrideApproved : false,
            }
          : item
      );
      const target = nextRows.find((item) => item.rowKey === row.rowKey);
      const maxStatus = getMaxHoursStatus(
        nextEmployeeId,
        target,
        nextRows,
        assignDialogWeekAssignments,
        employeesById,
        weekStartDay
      );
      if (maxStatus.status === 'over' && schedulingApprovalContext.isApprover) {
        updatedRows = nextRows;
        return nextRows;
      }
      validationMessage = getAssignmentValidationMessage(target, nextEmployeeId, nextRows);
      if (validationMessage) {
        return prevRows;
      }
      updatedRows = nextRows;
      return nextRows;
    });

    if (validationMessage) {
      setAssignmentRowErrors((prev) => ({
        ...prev,
        [row.rowKey]: validationMessage,
      }));
      showError(validationMessage);
      return;
    }

    if (updatedRows) {
      setAssignmentRowErrors((prev) => {
        const next = { ...prev };
        delete next[row.rowKey];
        return next;
      });
    }
  };

  const saveAssignments = async () => {
    if (!tenantId || !propertyId) return;
    const rowsToDelete = assignmentRows.filter((row) => row.employeeAssignmentId && !row.employeeId);
    const rowsToSave = assignmentRows.filter((row) => row.employeeId);
    if (!rowsToDelete.length && !rowsToSave.length) {
      showError('Nothing to save. Assign employees, remove assignments (Unassign then Save), or use Remove.');
      return;
    }

    const preValidationErrors = {};
    rowsToSave.forEach((row) => {
      const message = getAssignmentValidationMessage(row, row.employeeId, assignmentRows);
      if (message) {
        preValidationErrors[row.rowKey] = message;
      }
    });
    if (Object.keys(preValidationErrors).length) {
      setAssignmentRowErrors((prev) => ({ ...prev, ...preValidationErrors }));
      const firstMessage = Object.values(preValidationErrors)[0];
      showError(`Please fix assignment issues before saving. ${firstMessage}`);
      return;
    }

    try {
      setSavingAssignments(true);
      const headers = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const base = process.env.REACT_APP_BACKEND_URL;

      setAssignmentRowErrors((prev) => {
        const next = { ...prev };
        [...rowsToSave, ...rowsToDelete].forEach((row) => {
          delete next[row.rowKey];
        });
        return next;
      });

      const upserts = rowsToSave.map((row) => {
        const start = new Date(`${row.businessDate}T${toTimeKey(row.startTime)}`);
        let end = new Date(`${row.businessDate}T${toTimeKey(row.endTime)}`);
        if (end.getTime() <= start.getTime()) {
          end = new Date(end.getTime() + 86400000);
        }
        const durationHours = Math.max((end - start) / 3600000, 0);
        const assignedHours =
          row.allocation === 'PARTIAL' ? Number((durationHours / 2).toFixed(2)) : Number(durationHours.toFixed(2));

        const payload = {
          tenantId,
          propertyId,
          scheduleId: row.scheduleId || null,
          workforceScheduleId: row.workforceScheduleId || null,
          employeeId: row.employeeId,
          rolePositionId: row.rolePositionId,
          shiftMasterId: null,
          businessDate: row.businessDate,
          startTime: toTimeKey(row.startTime),
          endTime: toTimeKey(row.endTime),
          assignedHours,
          assignmentStatus: row.assignmentStatus || 'ASSIGNED',
          assignmentType: row.assignmentType || 'MANUAL',
          notes: `Allocation: ${row.allocation}`,
        };
        if (row.gmOverrideApproved) {
          payload.isApproved = true;
        }
        if (row.employeeAssignmentId) {
          payload.employeeAssignmentId = row.employeeAssignmentId;
        }
        return payload;
      });

      const batchRes = await fetch(`${base}conductor/employee-assignment/batch`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          deleteIds: rowsToDelete.map((r) => r.employeeAssignmentId),
          upserts,
        }),
      });
      if (!batchRes.ok) {
        const text = await batchRes.text().catch(() => '');
        throw new Error(parseApiErrorFromBodyText(text) || `Batch save failed (${batchRes.status})`);
      }
      const batchData = await batchRes.json();

      const deleteResults = (batchData.deleteResults || []).map((dr) =>
        dr.success
          ? { status: 'fulfilled' }
          : { status: 'rejected', reason: { message: dr.error || 'Could not remove this assignment.' } }
      );
      const saveResults = (batchData.upsertResults || []).map((ur) =>
        ur.success
          ? { status: 'fulfilled', value: { data: ur.assignment } }
          : { status: 'rejected', reason: { message: ur.error || 'Could not save this assignment.' } }
      );

      const allResults = [...deleteResults, ...saveResults];
      const failItems = allResults.filter((r) => r.status === 'rejected');

      const apiRowErrors = {};
      saveResults.forEach((r, i) => {
        if (r.status === 'rejected') {
          apiRowErrors[rowsToSave[i].rowKey] = r.reason?.message || 'Could not save this assignment.';
        }
      });
      deleteResults.forEach((r, i) => {
        if (r.status === 'rejected') {
          apiRowErrors[rowsToDelete[i].rowKey] = r.reason?.message || 'Could not remove this assignment.';
        }
      });
      if (Object.keys(apiRowErrors).length) {
        setAssignmentRowErrors((prev) => ({ ...prev, ...apiRowErrors }));
      }

      if (rowsToSave.length) {
        setAssignmentRows((prevRows) =>
          prevRows.map((row) => {
            const saveIdx = rowsToSave.findIndex((s) => s.rowKey === row.rowKey);
            if (saveIdx < 0) return row;
            const sr = saveResults[saveIdx];
            if (!sr || sr.status !== 'fulfilled' || !sr.value?.data) return row;
            return {
              ...row,
              employeeAssignmentId: sr.value.data.employeeAssignmentId || row.employeeAssignmentId,
            };
          })
        );
      }

      if (rowsToDelete.length) {
        setAssignmentRows((prevRows) =>
          prevRows.map((row) => {
            const delIdx = rowsToDelete.findIndex((d) => d.rowKey === row.rowKey);
            if (delIdx < 0) return row;
            const dr = deleteResults[delIdx];
            if (!dr || dr.status !== 'fulfilled') return row;
            return { ...row, employeeAssignmentId: null };
          })
        );
      }

      const delFail = deleteResults.filter((r) => r.status === 'rejected').length;
      const saveFail = saveResults.filter((r) => r.status === 'rejected').length;
      if (failItems.length) {
        showError(
          `${failItems.length} change(s) could not be saved. Check the highlighted rows in the grid for details (remove: ${delFail}, save: ${saveFail}).`
        );
      } else {
        const parts = [];
        if (rowsToDelete.length) parts.push(`${rowsToDelete.length} removed`);
        if (rowsToSave.length) parts.push(`${rowsToSave.length} saved`);
        showSuccess(parts.join('; ') || 'Done.');
        if (!saveFail && !delFail) {
          setAssignmentRowErrors({});
          setAssignDialogOpen(false);
        }
      }
      await fetchWorkforceCalendar();
      bumpCoverageDataRefresh();
    } catch (error) {
      showError(error.message || 'Failed to save assignments');
    } finally {
      setSavingAssignments(false);
    }
  };

  const moveWeek = (direction) => {
    const start = parseIsoDate(weekAnchorDate);
    start.setDate(start.getDate() + direction * 7);
    const next = getWeekStartDate(start, weekStartDay);
    setWeekAnchorDate(toIsoDate(next));
    if (viewMode === 'day') {
      setSelectedDayDate(toIsoDate(next));
    }
  };

  const moveDay = (direction) => {
    const d = parseIsoDate(selectedDayDate);
    d.setDate(d.getDate() + direction);
    const iso = toIsoDate(d);
    setSelectedDayDate(iso);
    setWeekAnchorDate(toIsoDate(getWeekStartDate(d, weekStartDay)));
  };

  const moveMonth = (direction) => {
    const d = parseIsoDate(`${selectedMonth}-01`);
    d.setMonth(d.getMonth() + direction, 1);
    setSelectedMonth(toIsoMonth(d));
    setWeekAnchorDate(toIsoDate(getWeekStartDate(d, weekStartDay)));
  };

  const openMonthDayPopup = (cell) => {
    if (!cell?.entries?.length) return;
    const date = parseIsoDate(cell.date);
    const dateLabel = date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    setMonthDayPopup({
      open: true,
      dateLabel,
      entries: cell.entries,
    });
  };

  useEffect(() => {
    if (!publishDialogOpen || !publishBatchId || !tenantId || !propertyId) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setPublishActionKey('refresh');
        const rows = await fetchPublishedSchedules(backendBase(), token, tenantId, propertyId, publishBatchId);
        if (!cancelled) {
          setPublishBatchRows(Array.isArray(rows) ? rows : []);
        }
      } catch (e) {
        if (!cancelled) {
          showError(e.message || 'Failed to load publish batch');
          setPublishBatchRows([]);
        }
      } finally {
        if (!cancelled) {
          setPublishActionKey(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishDialogOpen, publishBatchId, tenantId, propertyId]);

  const openPublishDialog = () => {
    if (!canEdit) return;
    if (!propertyId) {
      showError('Select a business unit first.');
      return;
    }
    setSendEmailOnPublish(loadPublishEmailPref(tenantId, propertyId));
    setPublishDialogOpen(true);
  };

  const createPublishDraftFromWeek = async () => {
    if (!tenantId || !propertyId) {
      showError('Select a business unit first.');
      return;
    }
    const entries = weekCalendarEntries.filter((e) => e.coverageId);
    if (!entries.length) {
      showError('No shifts in this week to add to a publish draft. Import coverage or add shifts first.');
      return;
    }
    try {
      setPublishActionKey('createDraft');
      const body = {
        tenantId,
        propertyId,
        scheduleId: null,
        fromDate: weekStartDate,
        toDate: weekEndDate,
        weekStartDate,
        weekEndDate,
        createdBy: null,
        notes: null,
      };
      const created = await createPublishedScheduleBatch(backendBase(), token, body);
      const arr = Array.isArray(created) ? created : [];
      setPublishBatchRows(arr);
      if (arr[0]?.publishBatchId) {
        setPublishBatchId(arr[0].publishBatchId);
        showSuccess(`Publish draft saved (version ${arr[0].versionNumber ?? '—'}). Schedule detail stays on the calendar.`);
      } else {
        showError('Publish draft response was unexpected.');
      }
    } catch (e) {
      showError(e.message || 'Failed to create publish draft');
    } finally {
      setPublishActionKey(null);
    }
  };

  const handlePublishSubmitForApproval = async () => {
    if (!publishBatchId) return;
    try {
      setPublishActionKey('submit');
      const rows = await submitPublishedBatchForApproval(backendBase(), token, tenantId, propertyId, publishBatchId);
      setPublishBatchRows(Array.isArray(rows) ? rows : []);
      showSuccess('Submitted for approval.');
    } catch (e) {
      showError(e.message || 'Submit failed');
    } finally {
      setPublishActionKey(null);
    }
  };

  const handlePublishApprove = async () => {
    if (!publishBatchId) return;
    try {
      setPublishActionKey('approve');
      const rows = await approvePublishedBatch(backendBase(), token, tenantId, propertyId, publishBatchId, null);
      setPublishBatchRows(Array.isArray(rows) ? rows : []);
      showSuccess('Batch approved.');
    } catch (e) {
      showError(e.message || 'Approve failed');
    } finally {
      setPublishActionKey(null);
    }
  };

  const handlePublishReject = async () => {
    if (!publishBatchId) return;
    try {
      setPublishActionKey('reject');
      const rows = await rejectPublishedBatch(backendBase(), token, tenantId, propertyId, publishBatchId, null);
      setPublishBatchRows(Array.isArray(rows) ? rows : []);
      showSuccess('Batch rejected.');
    } catch (e) {
      showError(e.message || 'Reject failed');
    } finally {
      setPublishActionKey(null);
    }
  };

  const handlePublishFinal = async () => {
    if (!publishBatchId) return;
    const status = publishBatchRows[0]?.approvalStatus;
    if (status !== 'APPROVED') {
      showError('Only approved batches can be published. Approve this batch first.');
      return;
    }
    try {
      setPublishActionKey('publish');
      savePublishEmailPref(tenantId, propertyId, sendEmailOnPublish);
      const rows = await publishPublishedBatch(
        backendBase(),
        token,
        tenantId,
        propertyId,
        publishBatchId,
        null,
        sendEmailOnPublish
      );
      setPublishBatchRows(Array.isArray(rows) ? rows : []);
      showSuccess(
        sendEmailOnPublish
          ? 'Published. Email notifications were requested for employees with shifts in this date range.'
          : 'Published (no emails sent).'
      );
    } catch (e) {
      showError(e.message || 'Publish failed');
    } finally {
      setPublishActionKey(null);
    }
  };

  const publishWorkflowBusy = Boolean(publishActionKey);
  const publishStatus = publishBatchRows[0]?.approvalStatus || '';
  const publishVersionLabel = publishBatchRows[0]?.versionNumber;

  const timelineRoleGroups = viewMode === 'day' ? dayTimelineRoleGroups : weekTimelineRoleGroups;
  const timelineIsoDates = viewMode === 'day' ? [selectedDayDate] : weekIsoDates;
  const timelineHeaderDates = viewMode === 'day' ? [parseIsoDate(selectedDayDate)] : weekDates;
  const timelineCollapsedKeys = viewMode === 'day' ? dayTimelineCollapsedRoleKeys : weeklyTimelineCollapsedRoleKeys;
  const toggleTimelineRoleCollapsed = viewMode === 'day' ? toggleDayTimelineRoleCollapsed : toggleWeeklyTimelineRoleCollapsed;
  const collapsibleTimelineKeys = viewMode === 'day' ? collapsibleDayRoleKeys : collapsibleWeeklyRoleKeys;
  const allTimelineCollapsibleCollapsed = viewMode === 'day' ? allDayCollapsibleRolesCollapsed : allWeeklyCollapsibleRolesCollapsed;
  const toggleTimelineCollapseAll = viewMode === 'day' ? toggleDayTimelineCollapseAll : toggleWeeklyTimelineCollapseAll;
  const timelineDayColumnCount = viewMode === 'day' ? 1 : Math.max(weekDates.length, 1);
  /** Single-day view: stretch the 24h strip to available width (no tiny pills). */
  const timelineFluidDay = timelineDayColumnCount === 1;

  const timelineVisibleRange = useMemo(() => {
    if (timelineFullDay) {
      return { startMin: 0, endMin: TIMELINE_MINUTES_PER_DAY };
    }
    let a = parseTimeInputToMinutes(timelineRangeStart);
    let b = parseTimeInputToMinutes(timelineRangeEnd);
    if (b <= a) b = Math.min(a + 60, TIMELINE_MINUTES_PER_DAY);
    return { startMin: a, endMin: b };
  }, [timelineFullDay, timelineRangeStart, timelineRangeEnd]);

  const timelineVisibleDurationMin = timelineVisibleRange.endMin - timelineVisibleRange.startMin;
  const timelineDayWidthPx = Math.max(
    (timelineVisibleDurationMin / 60) * WEEKLY_TIMELINE_HOUR_WIDTH,
    WEEKLY_TIMELINE_HOUR_WIDTH
  );
  const timelineHourTicks = useMemo(
    () => buildTimelineHourTicks(timelineVisibleRange.startMin, timelineVisibleRange.endMin),
    [timelineVisibleRange.startMin, timelineVisibleRange.endMin]
  );

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            fontSize: '0.9rem',
            '& .MuiBreadcrumbs-li': { color: ht.white },
            '& a': { color: ht.white },
            '& .MuiBreadcrumbs-separator': { color: ht.white },
          }}
        >
          <Link underline="hover" color="inherit" onClick={onBack} sx={{ cursor: 'pointer', color: ht.white }}>
            Workforce Operations
          </Link>
          <Typography sx={{ color: ht.white }}>Schedule</Typography>
        </Breadcrumbs>
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...harmonateCardSx,
          bgcolor: ht.white,
          border: `1px solid ${brandGreenBorder}`,
          p: 2.5,
          overflow: 'visible',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: 1.5,
            mb: 2,
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: '1 1 auto' }}>
            <ButtonGroup size="small" variant="outlined" sx={{ flexShrink: 0 }}>
              <Button
                variant={viewMode === 'day' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('day')}
                sx={viewMode === 'day' ? scheduleViewModeContainedSx : scheduleToolbarOutlinedGreenSx}
              >
                Day
              </Button>
              <Button
                variant={viewMode === 'weekly' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('weekly')}
                sx={viewMode === 'weekly' ? scheduleViewModeContainedSx : scheduleToolbarOutlinedGreenSx}
              >
                Weekly
              </Button>
              <Button
                variant={viewMode === 'monthly' ? 'contained' : 'outlined'}
                onClick={() => {
                  setSelectedMonth(toIsoMonth(parseIsoDate(weekStartDate)));
                  setViewMode('monthly');
                }}
                sx={viewMode === 'monthly' ? scheduleViewModeContainedSx : scheduleToolbarOutlinedGreenSx}
              >
                Monthly
              </Button>
            </ButtonGroup>
            <Typography
              variant="body2"
              color="text.secondary"
              title={businessUnitLabel ? `Business Unit: ${businessUnitLabel}` : 'No business unit selected'}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {businessUnitLabel ? `Business Unit: ${businessUnitLabel}` : 'No business unit selected'}
            </Typography>
          </Box>

          {(viewMode === 'weekly' || viewMode === 'day') && (
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'nowrap',
                flexShrink: 0,
                ml: 'auto',
                alignItems: 'center',
              }}
            >
              {viewMode === 'weekly' ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    if (!canAdd) return;
                    setAddDialogOpen(true);
                  }}
                  disabled={!propertyId || !canAdd}
                  sx={{ ...scheduleToolbarContainedGreenSx, whiteSpace: 'nowrap' }}
                >
                  + Add Position Coverage
                </Button>
              ) : null}
              <Button
                variant="contained"
                size="small"
                onClick={openAddShiftFromSchedule}
                disabled={!propertyId || fetchingRolesForAddShift || !canAdd}
                sx={{ ...scheduleToolbarContainedGreenSx, whiteSpace: 'nowrap' }}
              >
                {fetchingRolesForAddShift ? '…' : '+ Add Shift'}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={openAssignDialog}
                disabled={!propertyId || loadingAssignData || !canEdit}
                sx={{ ...scheduleToolbarContainedGreenSx, whiteSpace: 'nowrap' }}
              >
                + Assign
              </Button>
              {viewMode === 'weekly' ? (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={openPublishDialog}
                    disabled={!propertyId || !canEdit}
                    sx={{ ...scheduleToolbarContainedGreenSx, whiteSpace: 'nowrap' }}
                  >
                    Publish
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setEmployeeCoverageDialogOpen(true)}
                    disabled={!propertyId}
                    sx={{ ...scheduleToolbarContainedGreenSx, whiteSpace: 'nowrap' }}
                  >
                    Employee Coverage View
                  </Button>
                </>
              ) : null}
              <Button
                variant="contained"
                size="small"
                onClick={() => setClearScheduleDialogOpen(true)}
                disabled={!propertyId || clearScheduleLoading || !canDelete}
                sx={{ ...harmonateAbortButtonSx, whiteSpace: 'nowrap' }}
              >
                Clear Schedule
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() =>
                viewMode === 'monthly' ? moveMonth(-1) : viewMode === 'day' ? moveDay(-1) : moveWeek(-1)
              }
              sx={{ color: brandGreenDark }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {viewMode === 'weekly'
                ? `Weekly View ${weekRangeLabel ? `(${weekRangeLabel})` : ''}`
                : viewMode === 'day'
                ? `Day View (${parseIsoDate(selectedDayDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                  })})`
                : `Monthly View (${monthLabel})`}
            </Typography>
            <IconButton
              size="small"
              onClick={() =>
                viewMode === 'monthly' ? moveMonth(1) : viewMode === 'day' ? moveDay(1) : moveWeek(1)
              }
              sx={{ color: brandGreenDark }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
          {loadingCalendar ? <CircularProgress size={18} sx={{ color: ht.green }} /> : null}
        </Box>

        {viewMode === 'weekly' ? (
          <WorkforceWeekSummaryPanels
            tenantId={tenantId}
            propertyId={propertyId}
            weekStartDate={weekStartDate}
            weekDates={weekDates}
            refreshKey={weekSummaryRefreshKey}
          />
        ) : null}

        {(viewMode === 'weekly' || viewMode === 'day' || viewMode === 'monthly') && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Shift coverage
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#E8F5E9', borderLeft: '3px solid #2E7D32' }} />
              <Typography variant="caption" color="text.secondary">
                Full
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#FFF8E1', borderLeft: '3px solid #EF6C00' }} />
              <Typography variant="caption" color="text.secondary">
                Partial
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#FFEBEE', borderLeft: '3px solid #C62828' }} />
              <Typography variant="caption" color="text.secondary">
                Not assigned
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 0.5,
                  bgcolor: '#FFEBEE',
                  borderLeft: '3px solid #C62828',
                  boxShadow: 'inset 0 0 0 1px rgba(198, 40, 40, 0.35)',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                On leave (scheduled)
              </Typography>
            </Box>
          </Box>
        )}

        {scheduleLeaveConflicts.length > 0 ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {scheduleLeaveConflicts.length === 1
                ? '1 scheduled shift conflicts with employee leave — review and reschedule.'
                : `${scheduleLeaveConflicts.length} scheduled shifts conflict with employee leave — review and reschedule.`}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
              {scheduleLeaveConflicts.slice(0, 5).map((conflict) => (
                <Typography component="li" key={`${conflict.employeeId}|${conflict.businessDate}|${conflict.shiftLabel}`} variant="caption">
                  {conflict.employeeName} — {conflict.roleLabel} ({conflict.shiftLabel}) on{' '}
                  {formatDisplayDate(parseIsoDate(conflict.businessDate))}
                </Typography>
              ))}
            </Box>
            {scheduleLeaveConflicts.length > 5 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                +{scheduleLeaveConflicts.length - 5} more conflict
                {scheduleLeaveConflicts.length - 5 === 1 ? '' : 's'} shown on the schedule in red
              </Typography>
            ) : null}
          </Alert>
        ) : null}

        {(viewMode === 'weekly' || viewMode === 'day') && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 2,
              mb: 1.5,
            }}
          >
            <Tooltip title="Keep position names visible while scrolling the timeline horizontally">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={positionColumnFrozen}
                    onChange={(_, checked) => {
                      setPositionColumnFrozen(checked);
                      persistPositionColumnFrozen(checked);
                    }}
                    inputProps={{ 'aria-label': 'Freeze position column' }}
                    sx={harmonateSwitchSx}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Freeze positions
                  </Typography>
                }
              />
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Timeline
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={timelineFullDay}
                  onChange={(_, checked) => {
                    setTimelineFullDay(checked);
                    if (!checked) {
                      setTimelineRangeStart('08:00');
                      setTimelineRangeEnd('17:00');
                    }
                  }}
                  inputProps={{ 'aria-label': 'Show full 24-hour timeline' }}
                  sx={harmonateSwitchSx}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Full day (24h)
                </Typography>
              }
            />
            {!timelineFullDay ? (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  gap: 2,
                }}
              >
                <TextField
                  type="time"
                  label="From"
                  size="small"
                  value={timelineRangeStart}
                  onChange={(e) => {
                    setTimelineFullDay(false);
                    setTimelineRangeStart(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: 132,
                    '& .MuiOutlinedInput-root': { minHeight: 40, boxSizing: 'border-box' },
                  }}
                />
                <TextField
                  type="time"
                  label="To"
                  size="small"
                  value={timelineRangeEnd}
                  onChange={(e) => {
                    setTimelineFullDay(false);
                    setTimelineRangeEnd(e.target.value);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: 132,
                    '& .MuiOutlinedInput-root': { minHeight: 40, boxSizing: 'border-box' },
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setTimelineFullDay(false);
                    setTimelineRangeStart('08:00');
                    setTimelineRangeEnd('17:00');
                  }}
                  sx={{
                    ...scheduleToolbarOutlinedGreenSx,
                    minHeight: 40,
                    height: 40,
                    px: 1.5,
                    boxSizing: 'border-box',
                    textTransform: 'none',
                  }}
                >
                  8:00–5:00 default
                </Button>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary">
                Showing midnight–midnight (all hours)
              </Typography>
            )}
          </Box>
        )}

        {(viewMode === 'weekly' || viewMode === 'day') && (
          <Box
            sx={{
              border: `1px solid ${brandGreenBorder}`,
              borderRadius: 1,
              bgcolor: brandGreenSoft,
              width: '100%',
              minWidth: 0,
              overflowY: 'auto',
              overflowX: timelineFluidDay ? 'hidden' : 'auto',
            }}
          >
            {!timelineRoleGroups.length ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  No shifts
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: timelineFluidDay ? '100%' : 'auto',
                  minWidth: timelineFluidDay
                    ? 0
                    : WEEKLY_TIMELINE_LEFT_COL_WIDTH + timelineDayColumnCount * timelineDayWidthPx,
                  maxWidth: timelineFluidDay ? '100%' : 'none',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  sx={{
                    display: timelineFluidDay ? 'grid' : 'flex',
                    gridTemplateColumns: timelineFluidDay ? timelineFluidDayGridTemplate : undefined,
                    position: 'sticky',
                    top: 0,
                    zIndex: positionColumnFrozen ? 3 : 2,
                    bgcolor: brandGreenSoft,
                    borderBottom: `1px solid ${brandGreenBorder}`,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      width: timelineFluidDay ? 'auto' : WEEKLY_TIMELINE_LEFT_COL_WIDTH,
                      flexShrink: timelineFluidDay ? undefined : 0,
                      minWidth: 0,
                      p: 0.5,
                      borderRight: `1px solid ${brandGreenBorder}`,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      ...getTimelineLeftColFreezeSx({
                        frozen: positionColumnFrozen,
                        bgcolor: TIMELINE_STICKY_HEADER_BG,
                        isCorner: true,
                      }),
                    }}
                  >
                    {collapsibleTimelineKeys.length > 0 ? (
                      <Tooltip
                        title={
                          allTimelineCollapsibleCollapsed ? 'Expand all positions' : 'Collapse all positions'
                        }
                      >
                        <IconButton
                          size="small"
                          aria-expanded={!allTimelineCollapsibleCollapsed}
                          aria-label={
                            allTimelineCollapsibleCollapsed
                              ? 'Expand all positions'
                              : 'Collapse all positions'
                          }
                          onClick={toggleTimelineCollapseAll}
                          sx={{ color: brandGreenDark, mt: 0.25 }}
                        >
                          {allTimelineCollapsibleCollapsed ? (
                            <KeyboardArrowDownIcon fontSize="small" />
                          ) : (
                            <KeyboardArrowRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flex: timelineFluidDay ? undefined : 1,
                      minWidth: 0,
                      overflow: 'hidden',
                    }}
                  >
                    {timelineHeaderDates.map((d, dayIdx) => (
                      <Box
                        key={dayIdx}
                        sx={{
                          width: timelineFluidDay ? '100%' : timelineDayWidthPx,
                          flex: timelineFluidDay ? '1 1 0%' : undefined,
                          flexShrink: timelineFluidDay ? 1 : 0,
                          minWidth: timelineFluidDay ? 0 : undefined,
                          p: 0.5,
                          ...weeklyTimelineDayColumnSx(dayIdx, brandGreenBorder),
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                          {timelineDayColumnCount === 1
                            ? d.toLocaleDateString('en-US', { weekday: 'long' })
                            : weekDayLabels[dayIdx]}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          {formatDisplayDate(d)}
                        </Typography>
                        <Box sx={{ display: 'flex', width: '100%', minWidth: 0 }}>
                          {timelineHourTicks.map((tickMin, hIdx) => {
                            const hour24 = Math.floor(tickMin / 60) % 24;
                            const { hour12, suffix } = formatTimelineHourParts(hour24);
                            return (
                              <Box
                                key={tickMin}
                                sx={{
                                  width: timelineFluidDay ? undefined : WEEKLY_TIMELINE_HOUR_WIDTH,
                                  flex: timelineFluidDay ? '1 1 0%' : undefined,
                                  flexShrink: timelineFluidDay ? 1 : 0,
                                  minWidth: 0,
                                  textAlign: 'center',
                                  borderLeft: hIdx ? '1px solid rgba(0,0,0,0.06)' : 'none',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  py: 0.25,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: 9, fontWeight: 700, color: 'text.primary', lineHeight: 1.1 }}
                                >
                                  {hour12}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: 8, color: 'text.secondary', lineHeight: 1.1 }}
                                >
                                  {suffix}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {timelineRoleGroups.map((group) => {
                  const roleKey = getWeeklyTimelineRoleKey(group);
                  const subRowCount =
                    viewMode === 'weekly'
                      ? 0
                      : group.employeeRows.length + (group.unassignedBlocks.length ? 1 : 0);
                  const collapsible =
                    viewMode === 'day'
                      ? subRowCount >= 1
                      : viewMode !== 'weekly' && subRowCount > 1;
                  const collapsed = collapsible && timelineCollapsedKeys.has(roleKey);
                  const roleHeaderDaySum =
                    viewMode === 'day' && timelineIsoDates.length
                      ? group.coverageSummary?.scheduleSummaryByDay?.[timelineIsoDates[0]]
                      : null;

                  return (
                  <React.Fragment key={roleKey}>
                    <Box
                      sx={{
                        display: timelineFluidDay ? 'grid' : 'flex',
                        gridTemplateColumns: timelineFluidDay ? timelineFluidDayGridTemplate : undefined,
                        width: timelineFluidDay ? '100%' : undefined,
                        minWidth: timelineFluidDay ? 0 : undefined,
                        borderBottom: `1px solid ${brandGreenBorder}`,
                        bgcolor:
                          viewMode === 'day'
                            ? TIMELINE_DAY_ROLE_POSITION_ROW_BG
                            : 'rgba(0,0,0,0.04)',
                      }}
                    >
                      <Box
                        sx={{
                          width: timelineFluidDay ? 'auto' : WEEKLY_TIMELINE_LEFT_COL_WIDTH,
                          flexShrink: timelineFluidDay ? undefined : 0,
                          minWidth: 0,
                          p: 1,
                          borderRight: `1px solid ${brandGreenBorder}`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 0.5,
                          ...getTimelineLeftColFreezeSx({
                            frozen: positionColumnFrozen,
                            bgcolor:
                              viewMode === 'day'
                                ? TIMELINE_DAY_ROLE_POSITION_ROW_BG
                                : TIMELINE_STICKY_WEEKLY_ROLE_BG,
                          }),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 0.25 }}>
                          {collapsible ? (
                            <IconButton
                              size="small"
                              aria-expanded={!collapsed}
                              aria-label={
                                collapsed ? `Expand ${group.roleLabel}` : `Collapse ${group.roleLabel}`
                              }
                              onClick={() => toggleTimelineRoleCollapsed(roleKey)}
                              sx={{ p: 0.25, color: brandGreenDark }}
                            >
                              {collapsed ? (
                                <KeyboardArrowRightIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowDownIcon fontSize="small" />
                              )}
                            </IconButton>
                          ) : null}
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap title={group.roleLabel}>
                            {group.roleLabel}
                          </Typography>
                          {roleHeaderDaySum?.compact ? (
                            <IconButton
                              size="small"
                              aria-label={`Shift coverage summary for ${group.roleLabel}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setStaffDetailPopover(null);
                                setRoleCoverageSummaryPopover({
                                  anchorEl: e.currentTarget,
                                  roleLabel: group.roleLabel,
                                  dateLabel: formatDisplayDate(parseIsoDate(timelineIsoDates[0])),
                                  body: roleHeaderDaySum.full,
                                });
                              }}
                              sx={{ color: brandGreenDark, p: 0.35, flexShrink: 0 }}
                            >
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                        </Box>
                        {collapsible && collapsed ? (
                          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                            {subRowCount} rows
                          </Typography>
                        ) : null}
                      </Box>
                      <Box
                        sx={{
                          flex: timelineFluidDay ? undefined : 1,
                          minWidth: timelineFluidDay ? 0 : timelineDayColumnCount * timelineDayWidthPx,
                          position: 'relative',
                          minHeight: WEEKLY_TIMELINE_ROW_HEIGHT,
                          borderLeft: `1px solid ${brandGreenBorder}`,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          aria-hidden
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            zIndex: 0,
                            pointerEvents: 'none',
                            width: '100%',
                            minWidth: 0,
                          }}
                        >
                          {timelineIsoDates.map((_, dayIdx) => (
                            <Box
                              key={dayIdx}
                              sx={{
                                width: timelineFluidDay ? '100%' : timelineDayWidthPx,
                                flex: timelineFluidDay ? '1 1 0%' : undefined,
                                flexShrink: timelineFluidDay ? 1 : 0,
                                minWidth: timelineFluidDay ? 0 : undefined,
                                minHeight: '100%',
                                bgcolor:
                                  viewMode === 'day'
                                    ? 'transparent'
                                    : dayIdx % 2 === 0
                                    ? 'rgba(255,255,255,0.35)'
                                    : 'rgba(255,255,255,0.2)',
                                ...weeklyTimelineDayColumnSx(dayIdx, brandGreenBorder),
                              }}
                            />
                          ))}
                        </Box>
                        {viewMode === 'weekly' ? (
                          <Box
                            sx={{
                              position: 'relative',
                              zIndex: 1,
                              display: 'flex',
                              alignItems: 'stretch',
                              minWidth: 0,
                              flex: 1,
                              minHeight: WEEKLY_TIMELINE_ROW_HEIGHT,
                            }}
                          >
                            {timelineIsoDates.map((dayIso, dayIdx) => (
                              <Box
                                key={dayIdx}
                                sx={{
                                  position: 'relative',
                                  width: timelineFluidDay ? '100%' : timelineDayWidthPx,
                                  flex: timelineFluidDay ? '1 1 0%' : undefined,
                                  flexShrink: timelineFluidDay ? 1 : 0,
                                  minWidth: timelineFluidDay ? 0 : undefined,
                                  height: WEEKLY_TIMELINE_ROW_HEIGHT,
                                  bgcolor: dayIdx % 2 === 0 ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.35)',
                                  ...weeklyTimelineDayColumnSx(dayIdx, brandGreenBorder),
                                }}
                              >
                                {timelineHourTicks.map((tickMin) => (
                                  <Box
                                    key={tickMin}
                                    sx={{
                                      position: 'absolute',
                                      left: timelineFluidDay
                                        ? `${
                                            ((tickMin - timelineVisibleRange.startMin) /
                                              Math.max(timelineVisibleDurationMin, 1)) *
                                            100
                                          }%`
                                        : (tickMin - timelineVisibleRange.startMin) * (WEEKLY_TIMELINE_HOUR_WIDTH / 60),
                                      top: 0,
                                      bottom: 0,
                                      width: 1,
                                      bgcolor: 'rgba(0,0,0,0.05)',
                                    }}
                                  />
                                ))}
                                {(group.weeklyAggregatedBlocks || []).flatMap((block) =>
                                  block.segments
                                    .filter((seg) => seg.dateIso === dayIso)
                                    .map((seg, segIdx) => {
                                      const layout = getShiftSegmentLayout(
                                        seg.startMin,
                                        seg.endMin,
                                        timelineFluidDay,
                                        timelineVisibleRange
                                      );
                                      if (!layout) return null;
                                      const names = getAssignedEmployeeNamesForEntry(block.entry);
                                      const leaveConflictNames = getLeaveConflictNamesForEntry(block.entry);
                                      const leaveConflict = leaveConflictNames.length > 0;
                                      const displayCv = withLeaveConflictVisual(block.cv, leaveConflict);
                                      return (
                                        <ScheduleShiftCardShell
                                          key={`w-${block.entry.coverageId}-${dayIso}-${seg.startMin}-${segIdx}`}
                                          entry={block.entry}
                                          cv={displayCv}
                                          assignedNames={names}
                                          leaveConflictNames={leaveConflictNames}
                                          onOpenStaffDetail={openStaffDetailPopover}
                                          paperSx={leaveConflictPaperSx(
                                            {
                                              position: 'absolute',
                                              ...layout,
                                              top: 4,
                                              height: WEEKLY_TIMELINE_ROW_HEIGHT - 8,
                                              p: 0.25,
                                              overflow: 'hidden',
                                              borderColor: brandGreenBorder,
                                              bgcolor: displayCv.bg,
                                              borderLeft: `3px solid ${displayCv.color}`,
                                            },
                                            leaveConflict
                                          )}
                                        >
                                          <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{ fontSize: 10, display: 'block', lineHeight: 1.2 }}
                                          >
                                            {block.entry.shiftCode || block.entry.shiftName || 'Shift'}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{ fontSize: 9, color: 'text.secondary', display: 'block' }}
                                          >
                                            {formatDisplayTime(toTimeKey(block.entry.startTime))}–
                                            {formatDisplayTime(toTimeKey(block.entry.endTime))}
                                          </Typography>
                                        </ScheduleShiftCardShell>
                                      );
                                    })
                                )}
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              position: 'relative',
                              zIndex: 1,
                              display: 'flex',
                              alignItems: 'stretch',
                              minWidth: 0,
                              flex: 1,
                              minHeight: WEEKLY_TIMELINE_ROW_HEIGHT,
                            }}
                          >
                            {timelineIsoDates.map((dayIso, dayIdx) => (
                              <Box
                                key={dayIso}
                                sx={{
                                  position: 'relative',
                                  width: timelineFluidDay ? '100%' : timelineDayWidthPx,
                                  flex: timelineFluidDay ? '1 1 0%' : undefined,
                                  flexShrink: timelineFluidDay ? 1 : 0,
                                  minWidth: timelineFluidDay ? 0 : undefined,
                                  height: WEEKLY_TIMELINE_ROW_HEIGHT,
                                  bgcolor:
                                    viewMode === 'day'
                                      ? 'transparent'
                                      : dayIdx % 2 === 0
                                      ? 'rgba(255,255,255,0.65)'
                                      : 'rgba(255,255,255,0.35)',
                                  ...weeklyTimelineDayColumnSx(dayIdx, brandGreenBorder),
                                }}
                              >
                                {timelineHourTicks.map((tickMin) => (
                                  <Box
                                    key={tickMin}
                                    sx={{
                                      position: 'absolute',
                                      left: timelineFluidDay
                                        ? `${
                                            ((tickMin - timelineVisibleRange.startMin) /
                                              Math.max(timelineVisibleDurationMin, 1)) *
                                            100
                                          }%`
                                        : (tickMin - timelineVisibleRange.startMin) * (WEEKLY_TIMELINE_HOUR_WIDTH / 60),
                                      top: 0,
                                      bottom: 0,
                                      width: 1,
                                      bgcolor: 'rgba(0,0,0,0.05)',
                                    }}
                                  />
                                ))}
                                {(group.dayAggregatedBlocks || []).flatMap((block) =>
                                  block.segments
                                    .filter((seg) => seg.dateIso === dayIso)
                                    .map((seg, segIdx) => {
                                      const layout = getShiftSegmentLayout(
                                        seg.startMin,
                                        seg.endMin,
                                        timelineFluidDay,
                                        timelineVisibleRange
                                      );
                                      if (!layout) return null;
                                      const names = getAssignedEmployeeNamesForEntry(block.entry);
                                      const leaveConflictNames = getLeaveConflictNamesForEntry(block.entry);
                                      const leaveConflict = leaveConflictNames.length > 0;
                                      const displayCv = withLeaveConflictVisual(block.cv, leaveConflict);
                                      return (
                                        <ScheduleShiftCardShell
                                          key={`r-${roleKey}-${block.entry.coverageId}-${dayIso}-${seg.startMin}-${segIdx}`}
                                          variant="roleSummary"
                                          entry={block.entry}
                                          cv={displayCv}
                                          assignedNames={names}
                                          leaveConflictNames={leaveConflictNames}
                                          onOpenStaffDetail={openStaffDetailPopover}
                                          paperSx={leaveConflictPaperSx(
                                            {
                                              position: 'absolute',
                                              ...layout,
                                              top: 6,
                                              height: WEEKLY_TIMELINE_ROW_HEIGHT - 12,
                                              p: 0.25,
                                              overflow: 'hidden',
                                              borderColor: brandGreenBorder,
                                              bgcolor: displayCv.bg,
                                              borderLeft: `3px solid ${displayCv.color}`,
                                            },
                                            leaveConflict
                                          )}
                                        >
                                          <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{ fontSize: 9, display: 'block', lineHeight: 1.2 }}
                                          >
                                            {block.entry.shiftCode || block.entry.shiftName || 'Shift'}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            noWrap
                                            sx={{ fontSize: 8, color: 'text.secondary', display: 'block' }}
                                          >
                                            {formatDisplayTime(toTimeKey(block.entry.startTime))}–
                                            {formatDisplayTime(toTimeKey(block.entry.endTime))}
                                          </Typography>
                                        </ScheduleShiftCardShell>
                                      );
                                    })
                                )}
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {!collapsed &&
                      viewMode !== 'weekly' &&
                      group.employeeRows.map((row) => (
                      <Box
                        key={row.employeeId}
                        sx={{
                          display: timelineFluidDay ? 'grid' : 'flex',
                          gridTemplateColumns: timelineFluidDay ? timelineFluidDayGridTemplate : undefined,
                          width: timelineFluidDay ? '100%' : undefined,
                          minWidth: timelineFluidDay ? 0 : undefined,
                          borderBottom: `1px solid ${brandGreenBorder}`,
                          bgcolor:
                            viewMode === 'day' ? TIMELINE_DAY_RESOURCE_ROW_BG : undefined,
                        }}
                      >
                        <Box
                          sx={{
                            width: timelineFluidDay ? 'auto' : WEEKLY_TIMELINE_LEFT_COL_WIDTH,
                            flexShrink: timelineFluidDay ? undefined : 0,
                            minWidth: 0,
                            p: 1,
                            pl: 2.5,
                            borderRight: `1px solid ${brandGreenBorder}`,
                            display: 'flex',
                            alignItems: 'center',
                            ...getTimelineLeftColFreezeSx({
                              frozen: positionColumnFrozen,
                              bgcolor:
                                viewMode === 'day' ? TIMELINE_DAY_RESOURCE_ROW_BG : ht.white,
                            }),
                          }}
                        >
                          <Typography variant="caption" noWrap title={row.name} sx={{ flex: 1, minWidth: 0 }}>
                            {row.name}
                          </Typography>
                          {row.blocks.some((block) =>
                            block.segments.some((seg) =>
                              isEmployeeOnLeaveForDate(row.employeeId, seg.dateIso, employeeLeaveKeys)
                            )
                          ) ? (
                            <Tooltip title="Scheduled while on leave — rescheduling required">
                              <EventBusyIcon sx={{ fontSize: 16, color: '#C62828', ml: 0.5, flexShrink: 0 }} />
                            </Tooltip>
                          ) : null}
                          {employeeMaxHoursStatusById[row.employeeId]?.status === 'warning' ? (
                            <Tooltip title={`At max weekly hours (${employeeMaxHoursStatusById[row.employeeId].maxHours}h)`}>
                              <WarningAmberIcon sx={{ fontSize: 16, color: '#F9A825', ml: 0.5, flexShrink: 0 }} />
                            </Tooltip>
                          ) : null}
                          {employeeMaxHoursStatusById[row.employeeId]?.status === 'over' ? (
                            <Tooltip
                              title={`Over max weekly hours (${employeeMaxHoursStatusById[row.employeeId].projectedHours}h / ${employeeMaxHoursStatusById[row.employeeId].maxHours}h)`}
                            >
                              <ErrorOutlineIcon sx={{ fontSize: 16, color: '#C62828', ml: 0.5, flexShrink: 0 }} />
                            </Tooltip>
                          ) : null}
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flex: timelineFluidDay ? undefined : 1,
                            minWidth: timelineFluidDay ? 0 : timelineDayColumnCount * timelineDayWidthPx,
                            width: timelineFluidDay ? '100%' : 'auto',
                            overflow: 'hidden',
                          }}
                        >
                          {timelineIsoDates.map((dayIso, dayIdx) => (
                            <Box
                              key={dayIdx}
                              sx={{
                                position: 'relative',
                                width: timelineFluidDay ? '100%' : timelineDayWidthPx,
                                flex: timelineFluidDay ? '1 1 0%' : undefined,
                                flexShrink: timelineFluidDay ? 1 : 0,
                                minWidth: timelineFluidDay ? 0 : undefined,
                                height: WEEKLY_TIMELINE_ROW_HEIGHT,
                                bgcolor:
                                  viewMode === 'day'
                                    ? 'transparent'
                                    : dayIdx % 2 === 0
                                    ? 'rgba(255,255,255,0.65)'
                                    : 'rgba(255,255,255,0.35)',
                                ...weeklyTimelineDayColumnSx(dayIdx, brandGreenBorder),
                              }}
                            >
                              {timelineHourTicks.map((tickMin) => (
                                <Box
                                  key={tickMin}
                                  sx={{
                                    position: 'absolute',
                                    left: timelineFluidDay
                                      ? `${
                                          ((tickMin - timelineVisibleRange.startMin) /
                                            Math.max(timelineVisibleDurationMin, 1)) *
                                          100
                                        }%`
                                      : (tickMin - timelineVisibleRange.startMin) * (WEEKLY_TIMELINE_HOUR_WIDTH / 60),
                                    top: 0,
                                    bottom: 0,
                                    width: 1,
                                    bgcolor: 'rgba(0,0,0,0.05)',
                                  }}
                                />
                              ))}
                              {row.blocks.flatMap((block) =>
                                block.segments
                                  .filter((seg) => seg.dateIso === dayIso)
                                  .map((seg, segIdx) => {
                                    const layout = getShiftSegmentLayout(
                                      seg.startMin,
                                      seg.endMin,
                                      timelineFluidDay,
                                      timelineVisibleRange
                                    );
                                    if (!layout) return null;
                                    const names = getAssignedEmployeeNamesForEntry(block.entry);
                                    const leaveConflict = isEmployeeOnLeaveForDate(
                                      row.employeeId,
                                      dayIso,
                                      employeeLeaveKeys
                                    );
                                    const leaveConflictNames = leaveConflict
                                      ? [formatEmployeeName(calendarEmployeesById[row.employeeId]) || row.name]
                                      : [];
                                    const displayCv = withLeaveConflictVisual(block.cv, leaveConflict);
                                    return (
                                      <ScheduleShiftCardShell
                                        key={`${block.entry.coverageId}-${dayIso}-${seg.startMin}-${segIdx}`}
                                        entry={block.entry}
                                        cv={displayCv}
                                        assignedNames={names}
                                        leaveConflictNames={leaveConflictNames}
                                        onOpenStaffDetail={openStaffDetailPopover}
                                        paperSx={leaveConflictPaperSx(
                                          {
                                            position: 'absolute',
                                            ...layout,
                                            top: 4,
                                            height: WEEKLY_TIMELINE_ROW_HEIGHT - 8,
                                            p: 0.25,
                                            overflow: 'hidden',
                                            borderColor: brandGreenBorder,
                                            bgcolor: displayCv.bg,
                                            borderLeft: `3px solid ${displayCv.color}`,
                                          },
                                          leaveConflict
                                        )}
                                      >
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          sx={{ fontSize: 10, display: 'block', lineHeight: 1.2 }}
                                        >
                                          {block.entry.shiftCode || block.entry.shiftName || 'Shift'}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          sx={{ fontSize: 9, color: 'text.secondary', display: 'block' }}
                                        >
                                          {formatDisplayTime(toTimeKey(block.entry.startTime))}–
                                          {formatDisplayTime(toTimeKey(block.entry.endTime))}
                                        </Typography>
                                      </ScheduleShiftCardShell>
                                    );
                                  })
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      ))}

                    {!collapsed && viewMode !== 'weekly' && group.unassignedBlocks.length ? (
                      <Box
                        sx={{
                          display: timelineFluidDay ? 'grid' : 'flex',
                          gridTemplateColumns: timelineFluidDay ? timelineFluidDayGridTemplate : undefined,
                          width: timelineFluidDay ? '100%' : undefined,
                          minWidth: timelineFluidDay ? 0 : undefined,
                          borderBottom: `1px solid ${brandGreenBorder}`,
                          bgcolor:
                            viewMode === 'day' ? TIMELINE_DAY_RESOURCE_ROW_BG : undefined,
                        }}
                      >
                        <Box
                          sx={{
                            width: timelineFluidDay ? 'auto' : WEEKLY_TIMELINE_LEFT_COL_WIDTH,
                            flexShrink: timelineFluidDay ? undefined : 0,
                            minWidth: 0,
                            p: 1,
                            pl: 2.5,
                            borderRight: `1px solid ${brandGreenBorder}`,
                            display: 'flex',
                            alignItems: 'center',
                            ...getTimelineLeftColFreezeSx({
                              frozen: positionColumnFrozen,
                              bgcolor:
                                viewMode === 'day' ? TIMELINE_DAY_RESOURCE_ROW_BG : ht.white,
                            }),
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" fontStyle="italic">
                            Coverage needed
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flex: timelineFluidDay ? undefined : 1,
                            minWidth: timelineFluidDay ? 0 : timelineDayColumnCount * timelineDayWidthPx,
                            width: timelineFluidDay ? '100%' : 'auto',
                            overflow: 'hidden',
                          }}
                        >
                          {timelineIsoDates.map((dayIso, dayIdx) => (
                            <Box
                              key={dayIdx}
                              sx={{
                                position: 'relative',
                                width: timelineFluidDay ? '100%' : timelineDayWidthPx,
                                flex: timelineFluidDay ? '1 1 0%' : undefined,
                                flexShrink: timelineFluidDay ? 1 : 0,
                                minWidth: timelineFluidDay ? 0 : undefined,
                                height: WEEKLY_TIMELINE_ROW_HEIGHT,
                                bgcolor:
                                  viewMode === 'day'
                                    ? 'transparent'
                                    : dayIdx % 2 === 0
                                    ? 'rgba(255,255,255,0.65)'
                                    : 'rgba(255,255,255,0.35)',
                                ...weeklyTimelineDayColumnSx(dayIdx, brandGreenBorder),
                              }}
                            >
                              {timelineHourTicks.map((tickMin) => (
                                <Box
                                  key={tickMin}
                                  sx={{
                                    position: 'absolute',
                                    left: timelineFluidDay
                                      ? `${
                                          ((tickMin - timelineVisibleRange.startMin) /
                                            Math.max(timelineVisibleDurationMin, 1)) *
                                          100
                                        }%`
                                      : (tickMin - timelineVisibleRange.startMin) * (WEEKLY_TIMELINE_HOUR_WIDTH / 60),
                                    top: 0,
                                    bottom: 0,
                                    width: 1,
                                    bgcolor: 'rgba(0,0,0,0.05)',
                                  }}
                                />
                              ))}
                              {group.unassignedBlocks.flatMap((block) =>
                                block.segments
                                  .filter((seg) => seg.dateIso === dayIso)
                                  .map((seg, segIdx) => {
                                    const layout = getShiftSegmentLayout(
                                      seg.startMin,
                                      seg.endMin,
                                      timelineFluidDay,
                                      timelineVisibleRange
                                    );
                                    if (!layout) return null;
                                    const names = getAssignedEmployeeNamesForEntry(block.entry);
                                    return (
                                      <ScheduleShiftCardShell
                                        key={`u-${block.entry.coverageId}-${dayIso}-${seg.startMin}-${segIdx}`}
                                        entry={block.entry}
                                        cv={block.cv}
                                        assignedNames={names}
                                        onOpenStaffDetail={openStaffDetailPopover}
                                        paperSx={{
                                          position: 'absolute',
                                          ...layout,
                                          top: 4,
                                          height: WEEKLY_TIMELINE_ROW_HEIGHT - 8,
                                          p: 0.25,
                                          overflow: 'hidden',
                                          borderColor: brandGreenBorder,
                                          bgcolor: block.cv.bg,
                                          borderLeft: `3px solid ${block.cv.color}`,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          sx={{ fontSize: 10, display: 'block', lineHeight: 1.2 }}
                                        >
                                          {block.entry.shiftCode || block.entry.shiftName || 'Shift'}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          sx={{ fontSize: 9, color: 'text.secondary', display: 'block' }}
                                        >
                                          {formatDisplayTime(toTimeKey(block.entry.startTime))}–
                                          {formatDisplayTime(toTimeKey(block.entry.endTime))}
                                        </Typography>
                                      </ScheduleShiftCardShell>
                                    );
                                  })
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : null}
                  </React.Fragment>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {viewMode === 'monthly' && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: 1,
                mb: 1,
              }}
            >
              {monthDayLabels.map((day) => (
                <Typography key={day} variant="caption" sx={{ fontWeight: 700 }}>
                  {day.slice(0, 3)}
                </Typography>
              ))}
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: 1,
              }}
            >
              {monthlyCalendar.map((cell) => (
                <Paper
                  key={cell.key}
                  variant="outlined"
                  sx={{
                    minHeight: 120,
                    p: 0.75,
                    bgcolor: cell.empty ? '#fafafa' : brandGreenSoft,
                    opacity: cell.empty ? 0.6 : 1,
                    borderColor: brandGreenBorder,
                  }}
                >
                  {!cell.empty ? (
                    <>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {cell.dayNumber}
                      </Typography>
                      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {cell.entries.length ? (
                          cell.entries.slice(0, 3).map((entry) => {
                            const cv = getScheduleEntryCoverageVisual(entry);
                            const names = getAssignedEmployeeNamesForEntry(entry);
                            const leaveConflictNames = getLeaveConflictNamesForEntry(entry);
                            const leaveConflict = leaveConflictNames.length > 0;
                            const displayCv = withLeaveConflictVisual(cv, leaveConflict);
                            return (
                              <ScheduleShiftCardShell
                                key={entry.coverageId}
                                entry={entry}
                                cv={displayCv}
                                assignedNames={names}
                                leaveConflictNames={leaveConflictNames}
                                onOpenStaffDetail={openStaffDetailPopover}
                                paperSx={leaveConflictPaperSx(
                                  {
                                    p: 0.5,
                                    borderColor: brandGreenBorder,
                                    bgcolor: displayCv.bg,
                                    borderLeft: `3px solid ${displayCv.color}`,
                                  },
                                  leaveConflict
                                )}
                              >
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                  {entry.roleLabel}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  {formatDisplayTime(toTimeKey(entry.startTime))} - {formatDisplayTime(toTimeKey(entry.endTime))}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: displayCv.color, fontWeight: 600, fontSize: '0.65rem' }}>
                                  {leaveConflict ? 'On leave' : `${displayCv.assigned}/${displayCv.required}`}
                                </Typography>
                              </ScheduleShiftCardShell>
                            );
                          })
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No shifts
                          </Typography>
                        )}
                        {cell.entries.length > 3 && (
                          <Button
                            size="small"
                            onClick={() => openMonthDayPopup(cell)}
                            sx={{
                              mt: 0.25,
                              minWidth: 'unset',
                              px: 0.5,
                              color: brandGreenDark,
                              textTransform: 'none',
                              fontSize: '0.72rem',
                              justifyContent: 'flex-start',
                              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                            }}
                          >
                            +{cell.entries.length - 3} more
                          </Button>
                        )}
                      </Box>
                    </>
                  ) : null}
                </Paper>
              ))}
            </Box>
          </>
        )}
      </Paper>

      <Dialog
        open={addDialogOpen}
        onClose={() => !importing && setAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Add Position Coverage</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <TextField
            label="Select Week"
            type="week"
            size="small"
            fullWidth
            value={selectedWeek}
            onChange={(e) => {
              const monday = getMondayFromIsoWeek(e.target.value);
              setWeekAnchorDate(toIsoDate(getWeekStartDate(monday, weekStartDay)));
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 0.5 }}
          />
          <TextField
            label="Notes"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={importNotes}
            onChange={(e) => setImportNotes(e.target.value)}
            sx={{ mt: 1.5 }}
            placeholder="Enter notes"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Week Range: {weekRangeLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Importing also prepares this week for the next steps: review the schedule, submit for approval, and publish
            when ready (use the Publish options on the schedule).
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setAddDialogOpen(false)} disabled={importing} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={importRoleCoverage}
            disabled={importing || !propertyId}
            sx={harmonateProceedButtonSx}
          >
            {importing ? 'Importing...' : 'Import Coverage'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={publishDialogOpen}
        onClose={() => {
          if (publishWorkflowBusy) return;
          setPublishDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Publish schedule</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Week: <strong>{weekRangeLabel}</strong>
          </Typography>
          {!publishBatchId ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No publish batch in this session yet. Use Create draft from week ({weekCalendarEntries.length} shift row
              {weekCalendarEntries.length === 1 ? '' : 's'} on screen).
            </Typography>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Version: <strong>{publishVersionLabel ?? '—'}</strong>
                {' · '}
                Status: <strong>{publishStatus || '—'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Draft covers:{' '}
                <strong>
                  {publishBatchRows[0]?.weekStartDate && publishBatchRows[0]?.weekEndDate
                    ? `${publishBatchRows[0].weekStartDate} → ${publishBatchRows[0].weekEndDate}`
                    : weekRangeLabel}
                </strong>
                {' · '}
                Detail: use the weekly schedule (coverage is stored there).
              </Typography>
            </Box>
          )}

          {publishStatus === 'APPROVED' ? (
            <FormControlLabel
              sx={{ mb: 2, display: 'flex', alignItems: 'center' }}
              control={
                <Switch
                  checked={sendEmailOnPublish}
                  onChange={(_, checked) => setSendEmailOnPublish(checked)}
                  disabled={publishWorkflowBusy}
                  sx={harmonateSwitchSx}
                />
              }
              label={<Typography variant="body2">Email employees when publishing</Typography>}
            />
          ) : null}

          {publishStatus === 'REJECTED' ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              This batch was rejected. Create a new draft from the week to start again.
            </Typography>
          ) : null}

          {publishStatus === 'PUBLISHED' ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              This batch is already published.
            </Typography>
          ) : null}

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={createPublishDraftFromWeek}
              disabled={publishWorkflowBusy || !weekCalendarEntries.filter((e) => e.coverageId).length}
              sx={scheduleToolbarOutlinedGreenSx}
            >
              Create draft from week
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePublishSubmitForApproval}
              disabled={publishWorkflowBusy || !publishBatchId || publishStatus !== 'DRAFT'}
              sx={scheduleToolbarOutlinedGreenSx}
            >
              Submit for approval
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handlePublishApprove}
              disabled={publishWorkflowBusy || !publishBatchId || publishStatus !== 'PENDING_APPROVAL'}
              sx={scheduleToolbarOutlinedGreenSx}
            >
              Approve
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={handlePublishReject}
              disabled={publishWorkflowBusy || !publishBatchId || publishStatus !== 'PENDING_APPROVAL'}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handlePublishFinal}
              disabled={publishWorkflowBusy || !publishBatchId || publishStatus !== 'APPROVED'}
              sx={harmonateProceedButtonSx}
            >
              Publish
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => !publishWorkflowBusy && setPublishDialogOpen(false)}
            disabled={publishWorkflowBusy}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignDialogOpen}
        onClose={() => {
          if (savingAssignments || assignmentDeleteLoading) return;
          setAssignmentDeleteDialogOpen(false);
          setRowPendingAssignmentDelete(null);
          setAssignmentRowErrors({});
          setAssignDialogOpen(false);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          {viewMode === 'day' ? 'Assign employees — selected day' : 'Assign Employees - Weekly Schedule'}
        </DialogTitle>
        <DialogContent dividers sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {viewMode === 'day' ? (
                <>
                  Day:{' '}
                  {parseIsoDate(selectedDayDate).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                  })}
                </>
              ) : (
                <>Week: {weekRangeLabel}</>
              )}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  let dayIdx = 0;
                  if (viewMode === 'day') {
                    const d = parseIsoDate(selectedDayDate);
                    const mon = parseIsoDate(weekStartDate);
                    const diff = Math.round((d.getTime() - mon.getTime()) / 86400000);
                    dayIdx = Math.max(0, Math.min(weekDates.length - 1, diff));
                  }
                  setAddShiftForm((prev) => ({
                    ...prev,
                    dayIndex: viewMode === 'day' ? dayIdx : prev.dayIndex,
                    rolePositionId: assignRolePositions[0]?.rolePositionId || '',
                  }));
                  setAddShiftDialogOpen(true);
                }}
                disabled={savingAssignments || loadingAssignData || !assignRolePositions.length}
                sx={harmonateUploadOutlinedSx}
              >
                Add Shift
              </Button>
              <Button
                variant="outlined"
                onClick={autoAssignEmployees}
                disabled={savingAssignments || !assignmentRows.length}
                sx={harmonateUploadOutlinedSx}
              >
                Auto Assign
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '120px 1.2fr 1fr 1.3fr 0.7fr 0.85fr 44px',
              gap: 1,
              borderBottom: `1px solid ${brandGreenBorder}`,
              pb: 1,
              mb: 1,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Day</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Position</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Shift</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Employee</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Allocation</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>Status</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center' }}> </Typography>
          </Box>

          <Box sx={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {assignmentRows.length ? (
              assignmentRows.map((row) => {
                const baseRoleIds = eligibleEmployeeIdsByRole[row.rolePositionId] || [];
                const roleCandidates =
                  row.employeeId && !baseRoleIds.includes(row.employeeId)
                    ? [...baseRoleIds, row.employeeId]
                    : baseRoleIds;
                const rowError = assignmentRowErrors[row.rowKey];
                const maxHoursStatus = row.employeeId
                  ? getMaxHoursStatus(
                      row.employeeId,
                      row,
                      assignmentRows,
                      assignDialogWeekAssignments,
                      employeesById,
                      weekStartDay
                    )
                  : { status: 'ok' };
                const rowBorderColor =
                  rowError || maxHoursStatus.status === 'over'
                    ? '#C62828'
                    : maxHoursStatus.status === 'warning'
                      ? '#F9A825'
                      : brandGreenBorder;
                const rowBgColor =
                  rowError || maxHoursStatus.status === 'over'
                    ? '#FFF8F8'
                    : maxHoursStatus.status === 'warning'
                      ? '#FFFDE7'
                      : '#fff';
                return (
                  <Box
                    key={row.rowKey}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '120px 1.2fr 1fr 1.3fr 0.7fr 0.85fr 44px',
                      gap: 1,
                      p: 1,
                      border: `1px solid ${rowBorderColor}`,
                      borderRadius: 1,
                      bgcolor: rowBgColor,
                    }}
                  >
                    <Typography variant="caption">
                      {row.dayName} ({formatDisplayDate(parseIsoDate(row.businessDate))})
                    </Typography>
                    <Typography variant="caption">{row.roleLabel}</Typography>
                    <Typography variant="caption">{row.shiftLabel}</Typography>
                    <TextField
                      select
                      size="small"
                      value={row.employeeId || ''}
                      error={Boolean(rowError)}
                      onChange={(e) => handleEmployeeSelectionChange(row, e.target.value)}
                      SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                    >
                      <MenuItem
                        value=""
                        onClick={() => {
                          clearAssignmentRowError(row.rowKey);
                          setAssignmentRows((prev) =>
                            prev.map((item) =>
                              item.rowKey === row.rowKey
                                ? {
                                    ...item,
                                    employeeId: '',
                                    assignmentType: 'MANUAL',
                                  }
                                : item
                            )
                          );
                        }}
                      >
                        Unassigned
                      </MenuItem>
                      {roleCandidates.map((empId) => (
                        <MenuItem key={empId} value={empId}>
                          {formatEmployeeName(employeesById[empId])}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      value={row.allocation}
                      onChange={(e) => updateAssignmentRow(row.rowKey, 'allocation', e.target.value)}
                      SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                    >
                      <MenuItem value="FULL">Full</MenuItem>
                      <MenuItem value="PARTIAL">Partial</MenuItem>
                    </TextField>
                    <TextField
                      select
                      size="small"
                      value={row.assignmentStatus}
                      onChange={(e) => updateAssignmentRow(row.rowKey, 'assignmentStatus', e.target.value)}
                      SelectProps={{ MenuProps: harmonateSelectMenuProps }}
                    >
                      <MenuItem value="ASSIGNED">Assigned</MenuItem>
                      <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                      <MenuItem value="COMPLETED">Completed</MenuItem>
                      <MenuItem value="CANCELLED">Cancelled</MenuItem>
                      <MenuItem value="NO_SHOW">No Show</MenuItem>
                    </TextField>
                    <Tooltip title="Delete assignment">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Delete assignment"
                          onClick={() => openAssignmentDeleteDialog(row)}
                          disabled={savingAssignments || assignmentDeleteLoading}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {rowError ? (
                      <Typography
                        variant="caption"
                        sx={{ color: '#C62828', gridColumn: '1 / -1', mt: -0.25, fontWeight: 600 }}
                      >
                        {rowError}
                      </Typography>
                    ) : null}
                    {!rowError && maxHoursStatus.status === 'warning' ? (
                      <Typography
                        variant="caption"
                        sx={{ color: '#F57F17', gridColumn: '1 / -1', mt: -0.25, fontWeight: 600 }}
                      >
                        At max weekly hours ({maxHoursStatus.projectedHours}h / {maxHoursStatus.maxHours}h)
                      </Typography>
                    ) : null}
                    {!rowError && maxHoursStatus.status === 'over' ? (
                      <Box sx={{ gridColumn: '1 / -1', mt: -0.25 }}>
                        <Typography variant="caption" sx={{ color: '#C62828', fontWeight: 600, display: 'block' }}>
                          Over max weekly hours ({maxHoursStatus.projectedHours}h / {maxHoursStatus.maxHours}h).{' '}
                          {schedulingApprovalContext.isApprover
                            ? 'Approve override to save.'
                            : `${schedulingApprovalContext.approverRoleName || 'GM'} approval required.`}
                        </Typography>
                        {schedulingApprovalContext.isApprover ? (
                          <FormControlLabel
                            sx={{ mt: 0.25, ml: 0 }}
                            control={
                              <Checkbox
                                size="small"
                                checked={Boolean(row.gmOverrideApproved)}
                                onChange={(e) =>
                                  updateAssignmentRow(row.rowKey, 'gmOverrideApproved', e.target.checked)
                                }
                              />
                            }
                            label={
                              <Typography variant="caption">
                                GM approve override
                              </Typography>
                            }
                          />
                        ) : null}
                      </Box>
                    ) : null}
                  </Box>
                );
              })
            ) : (
              <Typography variant="body2" color="text.secondary">
                {viewMode === 'day'
                  ? 'No schedule rows for this day to assign.'
                  : 'No weekly schedule rows available for assignment.'}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setAssignmentRowErrors({});
              setAssignDialogOpen(false);
            }}
            disabled={savingAssignments}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveAssignments}
            disabled={
              savingAssignments ||
              !assignmentRows.length ||
              (!assignmentRows.some((r) => r.employeeId) &&
                !assignmentRows.some((r) => r.employeeAssignmentId && !r.employeeId))
            }
            sx={harmonateProceedButtonSx}
          >
            {savingAssignments ? 'Saving...' : 'Save Assignments'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignmentDeleteDialogOpen}
        onClose={closeAssignmentDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            {rowPendingAssignmentDelete?.employeeAssignmentId ? (
              <>
                Are you sure you want to delete this assignment for{' '}
                <strong>{rowPendingAssignmentDelete.roleLabel}</strong> —{' '}
                {rowPendingAssignmentDelete.shiftLabel} ({rowPendingAssignmentDelete.dayName})?
              </>
            ) : (
              <>
                Clear the selected employee for{' '}
                <strong>{rowPendingAssignmentDelete?.roleLabel}</strong> —{' '}
                {rowPendingAssignmentDelete?.shiftLabel} ({rowPendingAssignmentDelete?.dayName})? This row is not saved yet.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={closeAssignmentDeleteDialog} disabled={assignmentDeleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveAssignment}
            variant="contained"
            disabled={assignmentDeleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {assignmentDeleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={scheduleSlotDeleteDialogOpen}
        onClose={closeScheduleSlotDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Remove this shift from the schedule? Anyone assigned to this shift will be unassigned from it.
          </Typography>
          {entryPendingScheduleDelete ? (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="body2">
                <strong>{entryPendingScheduleDelete.roleLabel}</strong> —{' '}
                {(entryPendingScheduleDelete.shiftCode || entryPendingScheduleDelete.shiftName || 'Shift')}{' '}
                {formatDisplayTime(toTimeKey(entryPendingScheduleDelete.startTime))} -{' '}
                {formatDisplayTime(toTimeKey(entryPendingScheduleDelete.endTime))}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {entryPendingScheduleDelete.dayName} ({formatDisplayDate(parseIsoDate(entryPendingScheduleDelete.businessDate))})
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={closeScheduleSlotDeleteDialog} disabled={scheduleSlotDeleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteScheduleSlot}
            variant="contained"
            disabled={scheduleSlotDeleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {scheduleSlotDeleteLoading ? 'Removing...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearScheduleDialogOpen}
        onClose={closeClearScheduleDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Clear Schedule</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Remove all shifts and employee assignments for this property during the selected period? This cannot be undone.
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2">
              <strong>{viewMode === 'day' ? 'Day' : 'Week'}:</strong> {clearScheduleRangeLabel}
            </Typography>
            {businessUnitLabel ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Business Unit: {businessUnitLabel}
              </Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={closeClearScheduleDialog} disabled={clearScheduleLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={confirmClearSchedule}
            variant="contained"
            disabled={clearScheduleLoading}
            sx={harmonateAbortButtonSx}
          >
            {clearScheduleLoading ? 'Clearing...' : 'Clear Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addShiftDialogOpen}
        onClose={() => !addShiftSaving && setAddShiftDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          {viewMode === 'day'
            ? `Add shift — ${parseIsoDate(selectedDayDate).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}`
            : 'Add Shift (this week)'}
        </DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          {viewMode === 'day' ? (
            <TextField
              label="Day"
              size="small"
              fullWidth
              value={`${weekDayLabels[addShiftForm.dayIndex]} (${formatDisplayDate(weekDates[addShiftForm.dayIndex])})`}
              sx={{ mt: 1 }}
              InputProps={{ readOnly: true }}
            />
          ) : (
            <TextField
              select
              label="Day"
              size="small"
              fullWidth
              value={addShiftForm.dayIndex}
              onChange={(e) => setAddShiftForm((prev) => ({ ...prev, dayIndex: Number(e.target.value) }))}
              sx={{ mt: 1 }}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
            >
              {weekDayLabels.map((label, idx) => (
                <MenuItem key={label} value={idx}>
                  {label} ({formatDisplayDate(weekDates[idx])})
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            label="Position"
            size="small"
            fullWidth
            value={addShiftForm.rolePositionId}
            onChange={(e) => setAddShiftForm((prev) => ({ ...prev, rolePositionId: e.target.value }))}
            sx={{ mt: 2 }}
            SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          >
            {assignRolePositions.map((r) => (
              <MenuItem key={r.rolePositionId} value={r.rolePositionId}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Shift label"
            size="small"
            fullWidth
            value={addShiftForm.shiftLabel}
            onChange={(e) => setAddShiftForm((prev) => ({ ...prev, shiftLabel: e.target.value }))}
            sx={{ mt: 2 }}
            placeholder="e.g. Morning Shift"
          />
          <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
            <TextField
              label="Start"
              type="time"
              size="small"
              fullWidth
              value={addShiftForm.startTime}
              onChange={(e) => setAddShiftForm((prev) => ({ ...prev, startTime: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End"
              type="time"
              size="small"
              fullWidth
              value={addShiftForm.endTime}
              onChange={(e) => setAddShiftForm((prev) => ({ ...prev, endTime: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            label="Required headcount"
            type="number"
            size="small"
            fullWidth
            inputProps={{ min: 1 }}
            value={addShiftForm.requiredCount}
            onChange={(e) => setAddShiftForm((prev) => ({ ...prev, requiredCount: e.target.value }))}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {viewMode === 'day'
              ? 'Creates a schedule row for this property on the selected day. You can assign employees after saving.'
              : 'Creates a row in workforce schedule for this property and week. You can assign employees after saving.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setAddShiftDialogOpen(false)} disabled={addShiftSaving} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitAddShift}
            disabled={addShiftSaving || !addShiftForm.rolePositionId}
            sx={harmonateProceedButtonSx}
          >
            {addShiftSaving ? 'Adding…' : 'Add to schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={monthDayPopup.open}
        onClose={() => setMonthDayPopup({ open: false, dateLabel: '', entries: [] })}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Shifts - {monthDayPopup.dateLabel}</DialogTitle>
        <DialogContent dividers sx={harmonateDialogContentSx}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {monthDayPopup.entries.map((entry) => {
              const cv = getScheduleEntryCoverageVisual(entry);
              const names = getAssignedEmployeeNamesForEntry(entry);
              const leaveConflictNames = getLeaveConflictNamesForEntry(entry);
              const leaveConflict = leaveConflictNames.length > 0;
              const displayCv = withLeaveConflictVisual(cv, leaveConflict);
              return (
                <ScheduleShiftCardShell
                  key={entry.coverageId}
                  entry={entry}
                  cv={displayCv}
                  assignedNames={names}
                  leaveConflictNames={leaveConflictNames}
                  onOpenStaffDetail={openStaffDetailPopover}
                  paperSx={leaveConflictPaperSx(
                    {
                      p: 1,
                      borderColor: brandGreenBorder,
                      bgcolor: displayCv.bg,
                      borderLeft: `4px solid ${displayCv.color}`,
                    },
                    leaveConflict
                  )}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {entry.roleLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {(entry.shiftCode || entry.shiftName || 'Shift')} {formatDisplayTime(toTimeKey(entry.startTime))} -{' '}
                    {formatDisplayTime(toTimeKey(entry.endTime))}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: displayCv.color, fontWeight: 600 }}>
                    {leaveConflict
                      ? `On leave: ${leaveConflictNames.join(', ')}`
                      : `${displayCv.assigned}/${displayCv.required} · ${displayCv.label}`}
                  </Typography>
                </ScheduleShiftCardShell>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setMonthDayPopup({ open: false, dateLabel: '', entries: [] })}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(roleCoverageSummaryPopover)}
        anchorEl={roleCoverageSummaryPopover?.anchorEl}
        onClose={() => setRoleCoverageSummaryPopover(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, maxWidth: 400, borderRadius: 1 } }}
      >
        {roleCoverageSummaryPopover ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {roleCoverageSummaryPopover.roleLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {roleCoverageSummaryPopover.dateLabel}
            </Typography>
            <Typography
              variant="body2"
              component="div"
              sx={{ whiteSpace: 'pre-line', lineHeight: 1.5, fontSize: 13 }}
            >
              {roleCoverageSummaryPopover.body}
            </Typography>
          </Box>
        ) : null}
      </Popover>

      <Popover
        open={Boolean(staffDetailPopover)}
        anchorEl={staffDetailPopover?.anchorEl}
        onClose={() => setStaffDetailPopover(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1.5, maxWidth: 320, borderRadius: 1 } }}
      >
        {staffDetailPopover ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                {staffDetailPopover.entry.roleLabel}
              </Typography>
              <Tooltip title="Remove shift from schedule">
                <IconButton
                  size="small"
                  aria-label="Remove shift from schedule"
                  onClick={() => {
                    openScheduleSlotDeleteDialog(staffDetailPopover.entry);
                    setStaffDetailPopover(null);
                  }}
                  sx={{ color: 'error.main', mt: -0.5 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {(staffDetailPopover.entry.shiftCode || staffDetailPopover.entry.shiftName || 'Shift')}{' '}
              {formatDisplayTime(toTimeKey(staffDetailPopover.entry.startTime))} -{' '}
              {formatDisplayTime(toTimeKey(staffDetailPopover.entry.endTime))}
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, color: staffDetailPopover.cv.color, fontWeight: 600 }}
            >
              {staffDetailPopover.cv.assigned}/{staffDetailPopover.cv.required} · {staffDetailPopover.cv.label}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1.25 }}>
              Assigned staff
            </Typography>
            {staffDetailPopover.assignedNames.length ? (
              <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.25 }}>
                {staffDetailPopover.assignedNames.map((name, i) => (
                  <Typography key={`${name}-${i}`} component="li" variant="body2" sx={{ display: 'list-item' }}>
                    {name}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                No employees assigned
              </Typography>
            )}
          </Box>
        ) : null}
      </Popover>

      <EmployeeCoverageDialog
        open={employeeCoverageDialogOpen}
        onClose={() => setEmployeeCoverageDialogOpen(false)}
        propertyId={propertyId}
        refreshKey={coverageDataRefreshKey}
        defaultFromDate={weekStartDate}
        defaultToDate={weekEndDate}
      />

      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
      />
    </Box>
  );
};

export default Schedule;
